import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  InputGroup,
  ListGroup,
  Modal,
  Offcanvas,
  OverlayTrigger,
  Popover,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import {
  BookmarkPlus,
  Calendar,
  ChevronDown,
  ChevronUp,
  ClockHistory,
  EmojiSmile,
  Envelope,
  Image,
  InfoCircle,
  Link,
  ListTask,
  Lock,
  Paperclip,
  Plus,
  PersonWorkspace,
  Send,
  Stars,
  ThreeDotsVertical,
  Trash,
  X,
} from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { useTenantIdForPreferences } from '../hooks/useTenantIdForPreferences';
import { useUser } from '../contexts/UserContext';
import {
  useGetEaAssistantsQuery,
  useGetEaTasksQuery,
  useGetEaMemoryQuery,
  useGetEaActivityQuery,
  useGetEaScheduledEmailsQuery,
  useCreateEaAssistantMutation,
  useUpdateEaAssistantMutation,
  useCreateEaTaskMutation,
  useUpdateEaTaskMutation,
  useDeleteEaTaskMutation,
  useUpdateEaScheduledEmailMutation,
  useAppendEaActivityMutation,
  useCreateEaScheduledEmailMutation,
  useGetEaOwnerSendingPreferencesQuery,
  useUpdateEaOwnerSendingPreferencesMutation,
  useGetEaScheduleMessageScriptsQuery,
  useCreateEaScheduleMessageScriptMutation,
  useUpdateEaScheduleMessageScriptMutation,
  useRecordEaScheduleMessageScriptUseMutation,
  useDeleteEaScheduleMessageScriptMutation,
  useGetAccountsQuery,
  useGetTargetAccountsQuery,
  useGetClientsQuery,
  getExecutiveAssistantApiUrl,
} from '../services/api';
import {
  getDeviceIanaTimeZoneId,
  isValidIanaTimeZone,
  parseScheduleSendAt,
  utcInstantToZonedWallStrings,
} from '../utils/eaScheduleSendAt';
import {
  applyScriptPlaceholders,
  extractFirstNameFromAddressOrName,
  type ScriptPlaceholderContext,
} from '../utils/scriptTemplates';
import './ExecutiveAssistant.css';

const EA_SCHEDULE_COMPOSE_CONFIDENTIAL_PREFIX = '[Confidential]';
const EA_MAX_SCHEDULE_ATTACHMENTS = 10;
const EA_MAX_SCHEDULE_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const EA_MAX_SCHEDULE_ATTACHMENTS_TOTAL_BYTES = 6 * 1024 * 1024;

function formatScheduleAttachmentSize(bytes: number | undefined): string {
  const size = Number(bytes || 0);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function readScheduleFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || '');
      const commaIdx = raw.indexOf(',');
      resolve(commaIdx >= 0 ? raw.slice(commaIdx + 1) : raw);
    };
    reader.onerror = () => reject(reader.error || new Error(`Failed to read file ${file.name}`));
    reader.readAsDataURL(file);
  });
}

interface ScheduleComposeData {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
}

interface ScheduleComposeAttachment {
  id: string;
  name: string;
  contentType: string;
  size: number;
  contentBase64: string;
  lastModified: number;
}

/** Common region/city time zones for scheduling suggestions. */
const EA_SCHEDULE_COMMON_TIMEZONES: string[] = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Amsterdam',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Dubai',
  'Australia/Sydney',
  'Pacific/Auckland',
  'UTC',
];

/** City (or region) label for common zones — pairs with {@link EA_SCHEDULE_COMMON_TIMEZONES}. */
const EA_TIMEZONE_CITY_LABEL: Partial<Record<string, string>> = {
  'America/New_York': 'New York',
  'America/Chicago': 'Chicago',
  'America/Denver': 'Denver',
  'America/Los_Angeles': 'Los Angeles',
  'America/Phoenix': 'Phoenix',
  'America/Anchorage': 'Anchorage',
  'Pacific/Honolulu': 'Honolulu',
  'America/Toronto': 'Toronto',
  'America/Vancouver': 'Vancouver',
  'America/Mexico_City': 'Mexico City',
  'America/Sao_Paulo': 'São Paulo',
  'Europe/London': 'London',
  'Europe/Paris': 'Paris',
  'Europe/Berlin': 'Berlin',
  'Europe/Amsterdam': 'Amsterdam',
  'Asia/Tokyo': 'Tokyo',
  'Asia/Shanghai': 'Shanghai',
  'Asia/Singapore': 'Singapore',
  'Asia/Dubai': 'Dubai',
  'Australia/Sydney': 'Sydney',
  'Pacific/Auckland': 'Auckland',
  UTC: 'UTC',
};

/**
 * User-facing time zone phrase, e.g. "Eastern Time (New York)" when we can infer it;
 * otherwise the generic zone name from the runtime, or the stored id as a last resort.
 */
function scheduleTimeZoneUserLabel(timeZone: string, instant: Date): string {
  if (!timeZone.trim()) return 'your current time zone';
  if (!isValidIanaTimeZone(timeZone)) return timeZone;
  let longGeneric = '';
  try {
    longGeneric =
      new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longGeneric' })
        .formatToParts(instant)
        .find((p) => p.type === 'timeZoneName')
        ?.value?.trim() || '';
  } catch {
    /* ignore */
  }
  const city = EA_TIMEZONE_CITY_LABEL[timeZone];
  if (longGeneric && city) return `${longGeneric} (${city})`;
  if (longGeneric) return longGeneric;
  if (city) return city;
  return timeZone;
}

/** Short label like "EDT" / "GMT+9" from the runtime (varies by browser). */
function scheduleTimeZoneShort(timeZone: string, instant: Date): string {
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
        .formatToParts(instant)
        .find((p) => p.type === 'timeZoneName')
        ?.value?.trim() || ''
    );
  } catch {
    return '';
  }
}

type EaCurrentUserLite = { id: string; firstName?: string; lastName?: string; name?: string } | null | undefined;

function formatScheduledEmailCreatorLabel(createdByUserId: string | undefined, currentUser: EaCurrentUserLite): string {
  const uid = String(createdByUserId || '').trim();
  if (!uid) return '—';
  if (currentUser?.id && uid === String(currentUser.id).trim()) {
    const n = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ').trim();
    return n || String(currentUser.name || '').trim() || 'You';
  }
  return `Another user (…${uid.slice(-6)})`;
}

function scheduledEmailUpdatePayload(patch: Partial<ScheduledEmailRow>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const keys = [
    'recipient',
    'company',
    'subject',
    'body',
    'cc',
    'sendAt',
    'sendTimezone',
    'status',
    'replyStatus',
    'assistantId',
  ] as const;
  for (const k of keys) {
    if (patch[k] !== undefined) (out as Record<string, unknown>)[k] = patch[k];
  }
  return out;
}

/**
 * Controlled vocabulary — must match API `SCRIPT_LIBRARY_CATEGORIES` in
 * `schedule-message-scripts.ts` (expand there first, then here).
 */
const EA_SCRIPT_LIBRARY_CATEGORIES = [
  'Follow Up',
  'Cold Outreach',
  'Contract Renewal',
  'Winback',
  'Internal / Ops',
] as const;

const EA_SCRIPT_LIBRARY_MAX_TAGS = 15;
const EA_SCRIPT_TAG_MAX_LEN = 48;

function canonicalEaScriptLibraryCategory(raw: string): string | null {
  const t = raw.trim().toLowerCase();
  if (!t) return null;
  for (const c of EA_SCRIPT_LIBRARY_CATEGORIES) {
    if (c.toLowerCase() === t) return c;
  }
  return null;
}

/** Lowercase, trim, dedupe; caller enforces max count. */
function normalizeEaScriptLibraryTagsFromString(raw: string): string[] {
  const parts = raw
    .split(/[,]+/)
    .map((x) => x.trim().toLowerCase().slice(0, EA_SCRIPT_TAG_MAX_LEN))
    .filter(Boolean);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    if (seen.has(p)) continue;
    seen.add(p);
    out.push(p);
  }
  return out;
}

/** Display label for category badge (legacy rows may still have old free-text or “uncategorized”). */
function scriptLibraryCategoryBadgeLabel(raw: string | undefined): string {
  const t = (raw || '').trim();
  if (!t) return '—';
  if (t.toLowerCase() === 'uncategorized') return 'Legacy';
  return t;
}

type EaScriptLibraryStatus = 'active' | 'archived' | 'draft';

interface EaScheduleMessageScript {
  id: string;
  title: string;
  subject: string;
  body: string;
  updatedAt: string;
  category?: string;
  subcategory?: string;
  tags?: string[];
  useCase?: string;
  taskType?: string;
  tone?: string;
  audience?: string;
  status?: EaScriptLibraryStatus | string;
  createdBy?: string;
  createdAt?: string;
  useCount?: number;
}

function formatEaScriptUpdatedLabel(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

type EaScriptSortMode =
  | 'updated_desc'
  | 'updated_asc'
  | 'created_desc'
  | 'created_asc'
  | 'title_asc'
  | 'title_desc'
  | 'use_count_desc';

function filterAndSortEaScheduleScripts(
  scripts: EaScheduleMessageScript[],
  query: string,
  sort: EaScriptSortMode,
  filters: { category: string; status: string; tagsFilter: string }
): EaScheduleMessageScript[] {
  const q = query.trim().toLowerCase();
  const tagTokens = filters.tagsFilter
    .split(/[,]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);

  let list = [...scripts];

  if (filters.category.trim()) {
    const fc = filters.category.trim().toLowerCase();
    list = list.filter((s) => (s.category || '').trim().toLowerCase() === fc);
  }
  if (filters.status) {
    list = list.filter((s) => String(s.status || 'active') === filters.status);
  }
  if (tagTokens.length) {
    list = list.filter((s) => {
      const tagHay = (s.tags || []).join(' ');
      return tagTokens.every((tok) => tagHay.includes(tok));
    });
  }

  if (q) {
    list = list.filter((s) => {
      const hay =
        `${s.title}\n${s.subject}\n${s.body}\n${(s.tags || []).join(' ')}\n${s.useCase || ''}\n${s.category || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }

  const titleCmp = (a: EaScheduleMessageScript, b: EaScheduleMessageScript) =>
    a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
  const updatedCmp = (a: EaScheduleMessageScript, b: EaScheduleMessageScript) =>
    String(a.updatedAt || '').localeCompare(String(b.updatedAt || ''));
  const createdCmp = (a: EaScheduleMessageScript, b: EaScheduleMessageScript) =>
    String(a.createdAt || a.updatedAt || '').localeCompare(String(b.createdAt || b.updatedAt || ''));

  switch (sort) {
    case 'title_asc':
      list.sort(titleCmp);
      break;
    case 'title_desc':
      list.sort((a, b) => titleCmp(b, a));
      break;
    case 'updated_asc':
      list.sort(updatedCmp);
      break;
    case 'created_desc':
      list.sort((a, b) => createdCmp(b, a));
      break;
    case 'created_asc':
      list.sort(createdCmp);
      break;
    case 'use_count_desc':
      list.sort((a, b) => {
        const ua = Number(a.useCount) || 0;
        const ub = Number(b.useCount) || 0;
        if (ub !== ua) return ub - ua;
        return updatedCmp(b, a);
      });
      break;
    default:
      list.sort((a, b) => updatedCmp(b, a));
  }
  return list;
}

function buildEaScheduleScriptContext(
  assistants: ExecutiveAssistantProfile[],
  assistantId: string,
  toField: string
): ScriptPlaceholderContext {
  const a = assistants.find((x) => x.id === assistantId);
  const afn = String(a?.firstName || '').trim();
  const aln = String(a?.lastName || '').trim();
  return {
    recipientFirstName: extractFirstNameFromAddressOrName(toField),
    senderFullName: [afn, aln].filter(Boolean).join(' ').trim(),
    senderFirstName: afn,
  };
}

function firstDefinedError(errors: unknown[]): unknown {
  for (const e of errors) {
    if (e != null) return e;
  }
  return undefined;
}

function summarizeEaListFailure(err: unknown): { headline: string; body: string } {
  const fallback = {
    headline: 'Executive Assistant data unavailable',
    body: 'Could not load assistants, tasks, scheduled emails, or activity from the EA API.',
  };
  if (err == null) return fallback;
  if (typeof err === 'string') return { headline: fallback.headline, body: err };
  const any = err as { data?: { message?: string }; message?: string; error?: string; status?: number };
  let detail =
    (any?.data?.message && String(any.data.message)) ||
    (any?.message && String(any.message)) ||
    (any?.error && String(any.error)) ||
    '';
  if (detail && isEaUndeployedRouteSigV4Noise(detail)) {
    detail =
      'Owner prefs / message scripts routes are not deployed on the EA API yet. Deploy kg-api-executive-assistant (serverless-executive-assistant.yml). The app already sends Authorization: Bearer <JWT> like other EA calls; API Gateway returns a misleading SigV4 error when the path has no JWT method.';
  }
  if (!detail) {
    if (any?.status === 404) {
      return {
        headline: 'EA API error (404)',
        body: 'Executive Assistant stack may be outdated or misrouted. Deploy kg-api-executive-assistant (all routes) or set REACT_APP_EXECUTIVE_ASSISTANT_API_URL.',
      };
    }
    return fallback;
  }
  return {
    headline: any?.status ? `EA API error (${any.status})` : 'Executive Assistant API error',
    body: detail,
  };
}

/** API Gateway returns this for Bearer requests to paths that are not JWT-backed on the EA REST API (undeployed or wrong API). It is not a malformed client header. */
function isEaUndeployedRouteSigV4Noise(msg: string): boolean {
  return /Invalid key=value pair.*Authorization header/i.test(msg) && /missing equal-sign/i.test(msg);
}

/** User-visible text for failed EA RTK calls (schedule scripts, etc.). */
function eaRtkErrorDetail(err: unknown, fallback: string): string {
  if (err == null) return fallback;
  if (typeof err === 'string' && err.trim()) return err.trim();
  const errMsg =
    err instanceof Error
      ? String(err.message || '')
      : typeof err === 'object' && err !== null && 'message' in err
        ? String((err as { message?: unknown }).message || '')
        : '';
  const errName = err instanceof Error ? err.name : typeof err === 'object' && err !== null && 'name' in err ? String((err as { name?: unknown }).name) : '';
  if ((err instanceof TypeError || errName === 'TypeError') && /load failed|failed to fetch/i.test(errMsg)) {
    return 'Request blocked (often CORS preflight). On localhost, restart `npm start` so requests use the dev proxy at /executive-assistant (see src/setupProxy.js). On any origin, redeploy kg-api-executive-assistant (explicit GET/POST/… methods, not method: ANY).';
  }
  const any = err as {
    status?: number | string;
    data?: unknown;
    message?: string;
    error?: string;
  };
  const d = any.data;
  if (d && typeof d === 'object' && d !== null && 'message' in d) {
    const m = (d as { message?: unknown }).message;
    if (typeof m === 'string' && m.trim()) {
      if (isEaUndeployedRouteSigV4Noise(m)) {
        return (
          'These EA paths are not on the deployed Executive Assistant API yet (API Gateway treats them like SigV4/IAM, so a valid Bearer token looks “wrong”). ' +
          'Deploy kg-api-executive-assistant from serverless-executive-assistant.yml (staging/production) so owner-sending-preferences and schedule-message-scripts exist with the JWT authorizer, same as /assistants.'
        );
      }
      return m.trim();
    }
  }
  if (typeof d === 'string' && d.trim()) {
    return d.trim().replace(/<[^>]+>/g, '').slice(0, 280);
  }
  const st = typeof any.status === 'number' ? any.status : Number(any.status);
  if (st === 404) {
    return 'Executive Assistant API returned 404. Deploy kg-api-executive-assistant with the latest template (schedule-message-scripts routes + DynamoDB table), or set REACT_APP_EXECUTIVE_ASSISTANT_API_URL to the correct /executive-assistant base.';
  }
  if (st === 403) {
    const raw =
      (d && typeof d === 'object' && d !== null && 'message' in d && String((d as { message?: unknown }).message)) ||
      (any.message && String(any.message)) ||
      '';
    if (typeof raw === 'string' && isEaUndeployedRouteSigV4Noise(raw)) {
      return (
        'These EA paths are not on the deployed Executive Assistant API yet (API Gateway treats them like SigV4/IAM, so a valid Bearer token looks “wrong”). ' +
        'Deploy kg-api-executive-assistant from serverless-executive-assistant.yml (staging/production) so owner-sending-preferences and schedule-message-scripts exist with the JWT authorizer, same as /assistants.'
      );
    }
    return 'Executive Assistant API returned 403 — check API Gateway custom domain base path mapping for /executive-assistant.';
  }
  if (st === 401) return 'Unauthorized — sign in again.';
  if (any.message && String(any.message).trim()) return String(any.message).trim();
  if (any.error && String(any.error).trim()) return String(any.error).trim();
  return fallback;
}

const MAX_ASSISTANTS = 2;

type TaskStatus =
  | 'draft'
  | 'active'
  | 'waiting'
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'paused'
  | 'failed';

type TaskType = 'monitoring' | 'campaign_linked' | 'one_time_email' | 'monitoring_only';

export type TaskScope = 'private' | 'global';
export type CampaignMode = 'none' | 'link' | 'create';

type ScheduledEmailStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed' | 'canceled';

export interface ExecutiveAssistantProfile {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl: string;
  email: string;
  title: string;
  signature: string;
  toneProfile: string;
  introRules: string;
  skipReintroIfKnown: boolean;
  isActive: boolean;
  assignedTaskCount: number;
  pendingEmailCount: number;
  lastActivityAt: string | null;
}

export interface TaskProgressSummary {
  identified: number;
  queued: number;
  emailsSent: number;
  replies: number;
  followUpsPending: number;
  completed: number;
  errors: number;
}

export interface ExecutiveTask {
  id: string;
  name: string;
  assistantId: string;
  type: TaskType;
  objective: string;
  status: TaskStatus;
  priority: 'low' | 'normal' | 'high';
  scope: TaskScope;
  campaignMode: CampaignMode;
  linkedCampaignId: string | null;
  linkedCampaignName: string | null;
  accountId: string | null;
  clientId: string | null;
  contactId: string | null;
  nextActionAt: string | null;
  contactsLabel: string;
  lastStep: string;
  progress: TaskProgressSummary;
}

export interface AssistantMemoryRecord {
  id: string;
  assistantId: string;
  contactName: string;
  introducedAt: string | null;
  lastContactAt: string | null;
  lastReplyAt: string | null;
  conversationSummary: string;
  relationshipState: string;
  campaignOrTask: string;
}

export interface ScheduledEmailRow {
  id: string;
  assistantId: string;
  recipient: string;
  company: string;
  subject: string;
  body: string;
  cc: string;
  sendAt: string;
  /** Region/city time zone id when scheduling (persisted on create/update; absent on older rows). */
  sendTimezone?: string;
  status: ScheduledEmailStatus;
  replyStatus: string;
  /** Human-readable creator label (see `formatScheduledEmailCreatorLabel`). */
  createdBy: string;
  createdByUserId?: string;
}

function defaultProgress(): TaskProgressSummary {
  return { identified: 0, queued: 0, emailsSent: 0, replies: 0, followUpsPending: 0, completed: 0, errors: 0 };
}

function normalizeProgress(raw: unknown): TaskProgressSummary {
  if (!raw || typeof raw !== 'object') return defaultProgress();
  const p = raw as Record<string, unknown>;
  return {
    identified: Number(p.identified) || 0,
    queued: Number(p.queued) || 0,
    emailsSent: Number(p.emailsSent) || 0,
    replies: Number(p.replies) || 0,
    followUpsPending: Number(p.followUpsPending) || 0,
    completed: Number(p.completed) || 0,
    errors: Number(p.errors) || 0,
  };
}

function mapTaskFromApi(t: Record<string, unknown>): ExecutiveTask {
  const taskType = String(t.taskType || t.type || 'monitoring') as TaskType;
  const status = String(t.status || 'draft') as TaskStatus;
  const scope = (String(t.scope || 'private').toLowerCase() === 'global' ? 'global' : 'private') as TaskScope;
  const campaignMode = (['none', 'link', 'create'].includes(String(t.campaignMode || 'none').toLowerCase())
    ? String(t.campaignMode || 'none').toLowerCase()
    : 'none') as CampaignMode;
  return {
    id: String(t.id),
    name: String(t.name || ''),
    assistantId: String(t.assignedAssistantId || t.assistantId || ''),
    type: taskType,
    objective: String(t.objective || ''),
    status,
    priority: (String(t.priority || 'normal') as ExecutiveTask['priority']) || 'normal',
    scope,
    campaignMode,
    linkedCampaignId: t.campaignId ? String(t.campaignId) : null,
    linkedCampaignName: t.campaignName ? String(t.campaignName) : null,
    accountId: t.accountId ? String(t.accountId) : null,
    clientId: t.clientId ? String(t.clientId) : null,
    contactId: t.contactId ? String(t.contactId) : null,
    nextActionAt: t.nextActionAt ? String(t.nextActionAt) : null,
    contactsLabel: String(t.contactsLabel || '0 contacts'),
    lastStep: String(t.lastStep || ''),
    progress: normalizeProgress(t.progress),
  };
}

function mapAssistantFromApi(a: Record<string, unknown>): ExecutiveAssistantProfile {
  return {
    id: String(a.id),
    firstName: String(a.firstName || ''),
    lastName: String(a.lastName || ''),
    photoUrl: String(a.photoUrl || ''),
    email: String(a.email || ''),
    title: String(a.title || ''),
    signature: String(a.signature || ''),
    toneProfile: String(a.toneProfile || ''),
    introRules: String(a.introRules || ''),
    skipReintroIfKnown: Boolean(a.skipReintroIfKnown !== false),
    isActive: a.isActive !== false,
    assignedTaskCount: 0,
    pendingEmailCount: 0,
    lastActivityAt: a.lastActivityAt ? String(a.lastActivityAt) : null,
  };
}

function mapMemoryFromApi(m: Record<string, unknown>): AssistantMemoryRecord {
  return {
    id: String(m.id),
    assistantId: String(m.assistantId || ''),
    contactName: String(m.contactName || ''),
    introducedAt: m.introducedAt ? String(m.introducedAt) : null,
    lastContactAt: m.lastContactAt ? String(m.lastContactAt) : null,
    lastReplyAt: m.lastReplyAt ? String(m.lastReplyAt) : null,
    conversationSummary: String(m.conversationSummary || ''),
    relationshipState: String(m.relationshipState || ''),
    campaignOrTask: String(m.campaignOrTask || ''),
  };
}

function mapScheduledEmailFromApi(e: Record<string, unknown>, currentUser: EaCurrentUserLite): ScheduledEmailRow {
  const uid = e.createdByUserId ? String(e.createdByUserId) : '';
  return {
    id: String(e.id),
    assistantId: String(e.assistantId || ''),
    recipient: String(e.recipient || ''),
    company: String(e.company || '—'),
    subject: String(e.subject || ''),
    body: String(e.body ?? ''),
    cc: e.cc != null && String(e.cc).trim() ? String(e.cc).trim() : '',
    sendAt: String(e.sendAt || ''),
    sendTimezone: e.sendTimezone != null && String(e.sendTimezone).trim() ? String(e.sendTimezone).trim() : undefined,
    status: String(e.status || 'scheduled') as ScheduledEmailStatus,
    replyStatus: String(e.replyStatus || '—'),
    createdBy: formatScheduledEmailCreatorLabel(uid || undefined, currentUser),
    createdByUserId: uid || undefined,
  };
}

const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  waiting: 'Waiting',
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  completed: 'Completed',
  paused: 'Paused',
  failed: 'Failed',
};

const TASK_STATUS_VARIANT: Record<TaskStatus, string> = {
  draft: 'secondary',
  active: 'success',
  waiting: 'warning',
  scheduled: 'info',
  in_progress: 'primary',
  completed: 'dark',
  paused: 'secondary',
  failed: 'danger',
};

const EMAIL_STATUS_VARIANT: Record<ScheduledEmailStatus, string> = {
  draft: 'secondary',
  scheduled: 'info',
  sending: 'warning',
  sent: 'primary',
  failed: 'danger',
  canceled: 'secondary',
};

function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** Same instant, explicit UTC (for scheduled-send clarity). */
function formatSendAtUtcLine(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleString(undefined, { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'short' })} UTC`;
}

/** Same instant in the browser's local zone (label only; not a second source of truth). */
function formatSendAtLocalLine(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function progressPercent(p: TaskProgressSummary): number {
  const total = Math.max(1, p.identified);
  return Math.min(100, Math.round((p.completed / total) * 100));
}

const defaultAssistant = (): ExecutiveAssistantProfile => ({
  id: '',
  firstName: '',
  lastName: '',
  photoUrl: '',
  email: '',
  title: '',
  signature: '',
  toneProfile: '',
  introRules: '',
  skipReintroIfKnown: true,
  isActive: true,
  assignedTaskCount: 0,
  pendingEmailCount: 0,
  lastActivityAt: null,
});

const ExecutiveAssistantPage: React.FC = () => {
  const { currentUser } = useUser();
  const tenantId = useTenantIdForPreferences() || '';
  const skip = !tenantId;

  const { data: assistantsRaw = [], isLoading: loadingAssistants, isError: errAssistants, error: assistantsError } =
    useGetEaAssistantsQuery({ tenantId }, { skip });
  const { data: tasksRaw = [], isLoading: loadingTasks, isError: errTasks, error: tasksError } = useGetEaTasksQuery(
    { tenantId },
    { skip }
  );
  const { data: scheduledRaw = [], isLoading: loadingEmails, isError: errEmails, error: emailsError } =
    useGetEaScheduledEmailsQuery({ tenantId }, { skip });
  const { data: activityRaw = [], isLoading: loadingActivity, isError: errActivity, error: activityError } =
    useGetEaActivityQuery({ tenantId, limit: 200 }, { skip });

  const [memoryOffcanvas, setMemoryOffcanvas] = useState<{ assistantId: string } | null>(null);
  const { data: memoryRaw = [], isLoading: loadingMemory } = useGetEaMemoryQuery(
    { tenantId, assistantId: memoryOffcanvas?.assistantId || '' },
    { skip: skip || !memoryOffcanvas?.assistantId }
  );

  const [createAssistant] = useCreateEaAssistantMutation();
  const [updateAssistantMut] = useUpdateEaAssistantMutation();
  const [createTaskMut] = useCreateEaTaskMutation();
  const [updateTaskMut] = useUpdateEaTaskMutation();
  const [deleteTaskMut, { isLoading: deletingTask }] = useDeleteEaTaskMutation();
  const [updateScheduledEmailMut] = useUpdateEaScheduledEmailMutation();
  const [appendActivityMut] = useAppendEaActivityMutation();
  const [createScheduledEmailMut] = useCreateEaScheduledEmailMutation();
  const { data: ownerSendingPrefs } = useGetEaOwnerSendingPreferencesQuery({ tenantId }, { skip });
  const [updateOwnerSendingPrefsMut, { isLoading: savingOwnerSendingPrefs }] = useUpdateEaOwnerSendingPreferencesMutation();

  const tasks = useMemo(() => (Array.isArray(tasksRaw) ? tasksRaw.map((t) => mapTaskFromApi(t as any)) : []), [tasksRaw]);
  const scheduledEmails = useMemo(
    () =>
      Array.isArray(scheduledRaw) ? scheduledRaw.map((e) => mapScheduledEmailFromApi(e as any, currentUser)) : [],
    [scheduledRaw, currentUser]
  );
  const activity = useMemo(
    () =>
      (Array.isArray(activityRaw) ? activityRaw : []).map((a: any) => ({
        id: String(a.id),
        at: String(a.createdAt || ''),
        message: String(a.message || ''),
      })),
    [activityRaw]
  );
  const memory = useMemo(
    () => (Array.isArray(memoryRaw) ? memoryRaw.map((m) => mapMemoryFromApi(m as any)) : []),
    [memoryRaw]
  );

  const assistantsBase = useMemo(
    () => (Array.isArray(assistantsRaw) ? assistantsRaw.map((a) => mapAssistantFromApi(a as any)) : []),
    [assistantsRaw]
  );

  const assistants = useMemo(() => {
    return assistantsBase.map((a) => ({
      ...a,
      assignedTaskCount: tasks.filter((x) => x.assistantId === a.id && x.status !== 'completed' && x.status !== 'draft').length,
      pendingEmailCount: scheduledEmails.filter((x) => x.assistantId === a.id && (x.status === 'scheduled' || x.status === 'draft')).length,
    }));
  }, [assistantsBase, tasks, scheduledEmails]);

  /** Native `title` on disabled <button> often does not show; wrap + pointer-events:none on button. */
  const hasEaAssistants = assistants.length > 0;

  const anyEaListError = errAssistants || errTasks || errEmails || errActivity;
  const eaListFailureSummary = useMemo(() => {
    if (!anyEaListError) return null;
    const err = firstDefinedError([assistantsError, tasksError, emailsError, activityError]);
    return summarizeEaListFailure(err);
  }, [anyEaListError, assistantsError, tasksError, emailsError, activityError]);

  const [eaApiBannerDismissed, setEaApiBannerDismissed] = useState(false);
  useEffect(() => {
    setEaApiBannerDismissed(false);
  }, [tenantId]);

  const tasksRef = useRef<HTMLDivElement>(null);
  const emailsRef = useRef<HTMLDivElement>(null);

  const logActivity = useCallback(
    async (message: string) => {
      if (!tenantId) return;
      try {
        await appendActivityMut({ tenantId, message, visibilityScope: 'tenant' }).unwrap();
      } catch (e: any) {
        toast.error(e?.data?.message || 'Could not log activity');
      }
    },
    [appendActivityMut, tenantId]
  );

  const [showActivity, setShowActivity] = useState(false);
  const [assistantModal, setAssistantModal] = useState<ExecutiveAssistantProfile | null>(null);
  const [taskDetail, setTaskDetail] = useState<ExecutiveTask | null>(null);
  const [taskBeingEdited, setTaskBeingEdited] = useState<ExecutiveTask | null>(null);
  const [scheduledEmailDetail, setScheduledEmailDetail] = useState<ScheduledEmailRow | null>(null);
  const [scheduledEmailBeingEdited, setScheduledEmailBeingEdited] = useState<ScheduledEmailRow | null>(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [taskStatusFilter, setTaskStatusFilter] = useState<string>('all');
  /** While open, keep Assistants above Tasks (z-index); hover alone fails when pointer moves onto the menu. */
  const [assistantActionMenuOpenId, setAssistantActionMenuOpenId] = useState<string | null>(null);
  const [sendingPrefsModalOpen, setSendingPrefsModalOpen] = useState(false);

  const [alwaysCcOwnerOnEaSend, setAlwaysCcOwnerOnEaSend] = useState(false);
  const [alwaysCcEmail, setAlwaysCcEmail] = useState('');
  useEffect(() => {
    if (!ownerSendingPrefs) return;
    setAlwaysCcOwnerOnEaSend(Boolean((ownerSendingPrefs as { alwaysCcOwnerOnEaSend?: boolean }).alwaysCcOwnerOnEaSend));
    setAlwaysCcEmail(String((ownerSendingPrefs as { alwaysCcEmail?: string }).alwaysCcEmail || ''));
  }, [ownerSendingPrefs]);

  const saveOwnerSendingPreferences = async () => {
    if (!tenantId) return;
    try {
      await updateOwnerSendingPrefsMut({
        tenantId,
        alwaysCcOwnerOnEaSend,
        alwaysCcEmail: alwaysCcEmail.trim(),
      }).unwrap();
      await logActivity('Updated Executive Assistant sending preferences (always CC).');
      toast.success('Sending preferences saved.');
      setSendingPrefsModalOpen(false);
    } catch (e: any) {
      toast.error(e?.data?.message || 'Could not save preferences');
    }
  };

  const assistantById = useMemo(() => {
    const m = new Map<string, ExecutiveAssistantProfile>();
    assistants.forEach((a) => m.set(a.id, a));
    return m;
  }, [assistants]);

  const patchScheduledEmail = async (id: string, patch: Partial<ScheduledEmailRow>) => {
    if (!tenantId) return;
    const apiBody = scheduledEmailUpdatePayload(patch);
    if (Object.keys(apiBody).length === 0) return;
    try {
      await updateScheduledEmailMut({ tenantId, id, ...apiBody }).unwrap();
      await logActivity(`Updated scheduled email (${id.slice(0, 8)}…).`);
      setScheduledEmailDetail((prev) => (prev?.id === id ? { ...prev, ...patch } : prev));
    } catch (e: any) {
      toast.error(e?.data?.message || 'Update failed');
    }
  };

  const openAddAssistant = () => {
    if (assistants.length >= MAX_ASSISTANTS) {
      toast.info(`You can have up to ${MAX_ASSISTANTS} assistants.`);
      return;
    }
    setAssistantModal(defaultAssistant());
  };

  const saveAssistant = async (form: ExecutiveAssistantProfile) => {
    if (!tenantId) return;
    try {
      if (!form.id) {
        await createAssistant({
          tenantId,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          title: form.title,
          photoUrl: form.photoUrl,
          signature: form.signature,
          toneProfile: form.toneProfile,
          introRules: form.introRules,
          skipReintroIfKnown: form.skipReintroIfKnown,
          isActive: form.isActive,
        }).unwrap();
        await logActivity(`Added assistant ${form.firstName} ${form.lastName}.`);
        toast.success('Assistant created.');
      } else {
        await updateAssistantMut({
          tenantId,
          id: form.id,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          title: form.title,
          photoUrl: form.photoUrl,
          signature: form.signature,
          toneProfile: form.toneProfile,
          introRules: form.introRules,
          skipReintroIfKnown: form.skipReintroIfKnown,
          isActive: form.isActive,
        }).unwrap();
        await logActivity(`Updated assistant ${form.firstName} ${form.lastName}.`);
        toast.success('Assistant updated.');
      }
      setAssistantModal(null);
    } catch (e: any) {
      toast.error(e?.data?.message || 'Save failed');
    }
  };

  const toggleAssistantPause = async (a: ExecutiveAssistantProfile) => {
    if (!tenantId) return;
    try {
      await updateAssistantMut({ tenantId, id: a.id, isActive: !a.isActive }).unwrap();
      await logActivity(`${!a.isActive ? 'Resumed' : 'Paused'} assistant ${a.firstName} ${a.lastName}.`);
    } catch (e: any) {
      toast.error(e?.data?.message || 'Update failed');
    }
  };

  const filteredTasks = useMemo(() => {
    if (taskStatusFilter === 'all') return tasks;
    return tasks.filter((t) => t.status === taskStatusFilter);
  }, [tasks, taskStatusFilter]);

  const memoryForOffcanvas = useMemo(() => {
    if (!memoryOffcanvas) return [];
    return memory.filter((m) => m.assistantId === memoryOffcanvas.assistantId);
  }, [memory, memoryOffcanvas]);

  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const bootLoading = skip ? false : loadingAssistants || loadingTasks || loadingEmails || loadingActivity;

  if (!tenantId) {
    return (
      <div className="ea-hub">
        <div className="ea-body p-4 d-flex justify-content-center p-5">
          <Spinner animation="border" role="status" className="text-secondary">
            <span className="visually-hidden">Loading…</span>
          </Spinner>
        </div>
      </div>
    );
  }

  if (bootLoading && assistants.length === 0 && tasks.length === 0) {
    return (
      <div className="d-flex justify-content-center p-5">
        <Spinner animation="border" role="status" className="text-secondary">
          <span className="visually-hidden">Loading…</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="ea-hub">
      <div className="ea-hero">
        <div className="ea-hero-inner">
          <div className="ea-kicker">EMM · Automation layer</div>
          <h1 className="ea-title">Executive Assistant</h1>
          <p className="ea-sub">
            Create and manage up to two public-facing assistants that can handle task-based outreach, scheduled emails, and follow-up workflows on
            your behalf. Sequences are built in Campaigns; this is the control center for who is working, what they own, and how it is progressing.
            All data is loaded from the Executive Assistant API (no browser persistence).
          </p>
          <div className="ea-hero-actions">
            <Button className="ea-btn-gold" size="sm" onClick={openAddAssistant}>
              <Plus className="me-1" size={14} />
              Add Assistant
            </Button>
            <Button className="ea-btn-ghost" size="sm" variant="outline-light" onClick={() => scrollTo(tasksRef)}>
              <ListTask className="me-1" size={14} />
              View Task Queue
            </Button>
            <Button className="ea-btn-ghost" size="sm" variant="outline-light" onClick={() => scrollTo(emailsRef)}>
              <Envelope className="me-1" size={14} />
              View Scheduled Emails
            </Button>
            <Button className="ea-btn-ghost" size="sm" variant="outline-light" onClick={() => setShowActivity(true)}>
              <ClockHistory className="me-1" size={14} />
              Activity Log
            </Button>
          </div>
        </div>
      </div>

      <div className="ea-body">
        {anyEaListError && eaListFailureSummary && !eaApiBannerDismissed ? (
          <Alert
            variant="warning"
            dismissible
            onClose={() => setEaApiBannerDismissed(true)}
            className="shadow-sm mb-3"
            role="alert"
          >
            <Alert.Heading as="h6" className="mb-2">
              {eaListFailureSummary.headline}
            </Alert.Heading>
            <p className="small mb-2">{eaListFailureSummary.body}</p>
            <p className="small text-muted mb-0">
              <span className="fw-semibold text-body">EA API base URL in this build: </span>
              <code className="user-select-all small">{getExecutiveAssistantApiUrl()}</code>
            </p>
          </Alert>
        ) : null}
        <section
          className={`ea-section ea-section--assistants${assistantActionMenuOpenId ? ' ea-section--assistants-dropdown-open' : ''}`}
          aria-labelledby="ea-assistants-heading"
        >
          <div className="ea-section-head">
            <div>
              <h2 id="ea-assistants-heading" className="ea-section-title">
                Assistants
              </h2>
              <p className="ea-section-hint">Up to two personas per user in this tenant. Memory is assistant-specific.</p>
            </div>
          </div>
          <div className="ea-card-grid">
            {assistants.map((a) => (
              <Card key={a.id} className="ea-assistant-card border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex gap-3">
                    {a.photoUrl ? (
                      <img src={a.photoUrl} alt="" className="ea-avatar flex-shrink-0" />
                    ) : (
                      <div className="ea-avatar d-flex align-items-center justify-content-center flex-shrink-0 text-secondary">
                        <PersonWorkspace size={28} />
                      </div>
                    )}
                    <div className="flex-grow-1 min-w-0">
                      <div className="d-flex align-items-center gap-2 min-w-0">
                        <div className="fw-semibold text-truncate min-w-0 flex-grow-1">
                          {a.firstName} {a.lastName}
                        </div>
                        <div className="flex-shrink-0">
                          <Dropdown
                            align="end"
                            show={assistantActionMenuOpenId === a.id}
                            onToggle={(open) => setAssistantActionMenuOpenId(open ? a.id : null)}
                            onSelect={() => setAssistantActionMenuOpenId(null)}
                          >
                            <Dropdown.Toggle variant="light" size="sm" className="border py-0 px-2" id={`ea-act-${a.id}`}>
                              ···
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => setAssistantModal({ ...a })}>Edit profile</Dropdown.Item>
                              <Dropdown.Item onClick={() => toggleAssistantPause(a)}>{a.isActive ? 'Pause assistant' : 'Resume assistant'}</Dropdown.Item>
                              <Dropdown.Item onClick={() => setSendingPrefsModalOpen(true)}>Sending preferences</Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item onClick={() => scrollTo(tasksRef)}>View assigned tasks</Dropdown.Item>
                              <Dropdown.Item onClick={() => setMemoryOffcanvas({ assistantId: a.id })}>View conversation memory</Dropdown.Item>
                              <Dropdown.Item onClick={() => setShowActivity(true)}>Open activity log</Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </div>
                      <div className="small text-muted text-truncate">{a.email || 'No email set'}</div>
                      {a.title ? <div className="small mt-1 text-truncate">{a.title}</div> : null}
                      <div className="d-flex flex-wrap gap-2 mt-1">
                        <Badge bg={a.isActive ? 'success' : 'secondary'}>{a.isActive ? 'Active' : 'Paused'}</Badge>
                        {a.skipReintroIfKnown ? (
                          <Badge bg="light" text="dark" className="border">
                            Smart intro
                          </Badge>
                        ) : null}
                      </div>
                      <div className="ea-stat-row mt-2">
                        <span>
                          <strong>{a.assignedTaskCount}</strong> tasks
                        </span>
                        <span>
                          <strong>{a.pendingEmailCount}</strong> pending emails
                        </span>
                        <span>Last activity: {formatShortDate(a.lastActivityAt)}</span>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}
            {assistants.length < MAX_ASSISTANTS ? (
              <button type="button" className="ea-assistant-card ea-assistant-card--empty border-0 text-center p-3" onClick={openAddAssistant}>
                <div>
                  <Stars className="text-warning mb-2" size={28} />
                  <div className="fw-semibold text-dark">Add Assistant</div>
                  <div className="small text-muted mt-1">{MAX_ASSISTANTS - assistants.length} slot(s) remaining</div>
                </div>
              </button>
            ) : null}
          </div>
        </section>

        <div className="ea-dense-stack" ref={tasksRef}>
          <section className="ea-section ea-section--tasks" aria-labelledby="ea-tasks-heading">
            <div className="ea-section-head">
              <div>
                <h2 id="ea-tasks-heading" className="ea-section-title">
                  Tasks
                </h2>
                <p className="ea-section-hint">
                  Tasks are independent of campaigns unless you explicitly link or create one. Use scope to keep work private or visible to the tenant.
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Form.Select size="sm" style={{ width: 160 }} value={taskStatusFilter} onChange={(e) => setTaskStatusFilter(e.target.value)} aria-label="Filter by status">
                  <option value="all">All statuses</option>
                  {(Object.keys(TASK_STATUS_LABEL) as TaskStatus[]).map((k) => (
                    <option key={k} value={k}>
                      {TASK_STATUS_LABEL[k]}
                    </option>
                  ))}
                </Form.Select>
                {hasEaAssistants ? (
                  <Button size="sm" variant="primary" onClick={() => setTaskModalOpen(true)}>
                    <Plus size={14} className="me-1" />
                    New task
                  </Button>
                ) : (
                  <span
                    className="d-inline-block"
                    title="Add an assistant first. Tasks are assigned to one of your assistants."
                    style={{ cursor: 'not-allowed' }}
                  >
                    <Button
                      size="sm"
                      variant="primary"
                      disabled
                      style={{ pointerEvents: 'none' }}
                      aria-describedby="ea-assistants-heading"
                    >
                      <Plus size={14} className="me-1" />
                      New task
                    </Button>
                  </span>
                )}
              </div>
            </div>
            <div className="ea-table-wrap">
              <Table hover className="align-middle mb-0 ea-table">
                <thead className="table-light">
                  <tr>
                    <th>Task</th>
                    <th>Assistant</th>
                    <th>Type</th>
                    <th>Scope</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Progress</th>
                    <th>Next action</th>
                    <th>Campaign</th>
                    <th>Contacts</th>
                    <th>Last step</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.length === 0 ? (
                    <tr className="ea-task-empty-row">
                      <td colSpan={11} className="text-center text-muted py-4">
                        {assistants.length === 0
                          ? 'No tasks match this filter. Add an assistant to create your own, or ask a teammate to share a global task.'
                          : 'No tasks match this filter. Create one to get started.'}
                      </td>
                    </tr>
                  ) : (
                    filteredTasks.map((t) => {
                      const asst = assistantById.get(t.assistantId);
                      return (
                        <tr
                          key={t.id}
                          role="button"
                          onClick={() => {
                            setScheduledEmailDetail(null);
                            setTaskDetail(t);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="fw-medium">{t.name}</td>
                          <td>{asst ? `${asst.firstName} ${asst.lastName}` : '—'}</td>
                          <td className="text-capitalize small">{t.type.replace(/_/g, ' ')}</td>
                          <td>
                            <Badge bg={t.scope === 'global' ? 'info' : 'secondary'}>{t.scope === 'global' ? 'Global' : 'Private'}</Badge>
                          </td>
                          <td>
                            <Badge bg={TASK_STATUS_VARIANT[t.status]}>{TASK_STATUS_LABEL[t.status]}</Badge>
                          </td>
                          <td className="text-capitalize small">{t.priority}</td>
                          <td style={{ minWidth: 120 }}>
                            <div className="ea-progress-mini mb-1">
                              <div style={{ width: `${progressPercent(t.progress)}%` }} />
                            </div>
                            <span className="small text-muted">{progressPercent(t.progress)}% done</span>
                          </td>
                          <td className="small">{formatShortDate(t.nextActionAt)}</td>
                          <td className="small">{t.linkedCampaignName || '—'}</td>
                          <td className="small">{t.contactsLabel}</td>
                          <td className="small text-muted">{t.lastStep}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </section>

          <section className="ea-section ea-section--scheduled" ref={emailsRef} aria-labelledby="ea-scheduled-heading">
            <div className="ea-section-head">
              <div>
                <h2 id="ea-scheduled-heading" className="ea-section-title">
                  Scheduled emails
                </h2>
                <p className="ea-section-hint">One-off sends without building a campaign.</p>
              </div>
              {hasEaAssistants ? (
                <Button size="sm" variant="outline-primary" onClick={() => setScheduleModalOpen(true)}>
                  <Plus size={14} className="me-1" />
                  Schedule email
                </Button>
              ) : (
                <span
                  className="d-inline-block"
                  title="Add an assistant first. Scheduled email is sent from an assistant persona."
                  style={{ cursor: 'not-allowed' }}
                >
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled
                    style={{ pointerEvents: 'none' }}
                    aria-describedby="ea-assistants-heading"
                  >
                    <Plus size={14} className="me-1" />
                    Schedule email
                  </Button>
                </span>
              )}
            </div>
            {assistants.length === 0 ? (
              <AlertInline message="Add an assistant to schedule outbound email." />
            ) : (
              <div className="ea-table-wrap">
                <Table hover className="align-middle mb-0 ea-table">
                  <thead className="table-light">
                    <tr>
                      <th>Recipient</th>
                      <th>Company</th>
                      <th>Assistant</th>
                      <th>Subject</th>
                      <th>Send at</th>
                      <th>Status</th>
                      <th>Reply</th>
                      <th title="The KG user account that created this scheduled send (not the assistant persona)">Created by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduledEmails.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center text-muted py-4">
                          No scheduled emails yet.
                        </td>
                      </tr>
                    ) : (
                      scheduledEmails.map((e) => {
                        const asst = assistantById.get(e.assistantId);
                        return (
                          <tr
                            key={e.id}
                            role="button"
                            onClick={() => {
                              setTaskDetail(null);
                              setScheduledEmailDetail(e);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <td className="small">{e.recipient}</td>
                            <td className="small">{e.company}</td>
                            <td className="small">{asst ? `${asst.firstName} ${asst.lastName}` : '—'}</td>
                            <td className="small">{e.subject}</td>
                            <td className="small">{formatShortDate(e.sendAt)}</td>
                            <td>
                              <Badge bg={EMAIL_STATUS_VARIANT[e.status]}>{e.status}</Badge>
                            </td>
                            <td className="small">{e.replyStatus}</td>
                            <td className="small">{e.createdBy}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </section>
        </div>
      </div>

      <AssistantEditModal
        show={assistantModal != null}
        initial={assistantModal}
        onHide={() => setAssistantModal(null)}
        onSave={saveAssistant}
      />

      <Modal show={sendingPrefsModalOpen} onHide={() => setSendingPrefsModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Sending preferences</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-3">
            Applies to <strong>your user</strong> in this tenant for Executive Assistant sends (e.g. scheduled emails), not only the assistant you
            opened this from.
          </p>
          <Form.Check
            type="switch"
            id="ea-always-cc-modal-toggle"
            checked={alwaysCcOwnerOnEaSend}
            onChange={(e) => setAlwaysCcOwnerOnEaSend(e.target.checked)}
            label="Always CC this email address when Executive Assistant sends"
            className="mb-3"
          />
          <Form.Group className="mb-0">
            <Form.Label className="small">CC address (optional)</Form.Label>
            <Form.Control
              type="email"
              size="sm"
              value={alwaysCcEmail}
              onChange={(e) => setAlwaysCcEmail(e.target.value)}
              placeholder={
                (ownerSendingPrefs as { defaultCcEmailHint?: string } | null)?.defaultCcEmailHint
                  ? `Default: ${(ownerSendingPrefs as { defaultCcEmailHint?: string }).defaultCcEmailHint}`
                  : 'Leave blank to use your login email'
              }
            />
            <Form.Text className="text-muted">When the switch is on, new scheduled emails include this Cc on the row (merged with any other Cc).</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSendingPrefsModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={savingOwnerSendingPrefs} onClick={() => void saveOwnerSendingPreferences()}>
            {savingOwnerSendingPrefs ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <TaskWizardModal
        show={taskModalOpen}
        assistants={assistants}
        tenantId={tenantId}
        onHide={() => setTaskModalOpen(false)}
        onCreate={async (payload) => {
          try {
            await createTaskMut(payload).unwrap();
            await logActivity(`Created task "${payload.name}".`);
            setTaskModalOpen(false);
            toast.success('Task saved.');
          } catch (e: any) {
            toast.error(e?.data?.message || 'Could not create task');
          }
        }}
      />

      <TaskEditModal
        show={taskBeingEdited != null}
        task={taskBeingEdited}
        assistants={assistants}
        tenantId={tenantId}
        onHide={() => setTaskBeingEdited(null)}
        onSave={async (id, body) => {
          await updateTaskMut({ id, ...body }).unwrap();
          await logActivity(`Updated task "${String(body.name || '')}".`);
          setTaskBeingEdited(null);
        }}
      />

      <ScheduleEmailModal
        show={scheduleModalOpen}
        assistants={assistants}
        tenantId={tenantId}
        onHide={() => setScheduleModalOpen(false)}
        onCreate={async (body) => {
          try {
            await createScheduledEmailMut(body).unwrap();
            await logActivity(`Scheduled email "${body.subject}".`);
            setScheduleModalOpen(false);
            toast.success('Email scheduled.');
          } catch (e: any) {
            toast.error(e?.data?.message || 'Could not schedule email');
          }
        }}
      />

      <Modal show={showActivity} onHide={() => setShowActivity(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Activity log</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 420, overflowY: 'auto' }}>
          {loadingActivity ? (
            <Spinner size="sm" animation="border" />
          ) : activity.length === 0 ? (
            <p className="text-muted mb-0">No activity yet.</p>
          ) : (
            <ul className="list-unstyled mb-0">
              {activity.map((a) => (
                <li key={a.id} className="border-bottom py-2">
                  <div className="small text-muted">{formatShortDate(a.at)}</div>
                  <div>{a.message}</div>
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
      </Modal>

      <Offcanvas show={memoryOffcanvas != null} onHide={() => setMemoryOffcanvas(null)} placement="end" style={{ width: 'min(440px, 100vw)' }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Conversation memory</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <p className="small text-muted">Per-assistant relationship state from the API.</p>
          {loadingMemory ? (
            <Spinner size="sm" animation="border" />
          ) : memoryForOffcanvas.length === 0 ? (
            <p className="text-muted">No memory records for this assistant yet.</p>
          ) : (
            memoryForOffcanvas.map((m) => (
              <Card key={m.id} className="mb-3 border shadow-sm">
                <Card.Body className="py-3">
                  <div className="fw-semibold">{m.contactName}</div>
                  <Badge bg="light" text="dark" className="border mt-2">
                    {m.relationshipState}
                  </Badge>
                  <div className="small text-muted mt-2">Introduced: {formatShortDate(m.introducedAt)}</div>
                  <div className="small text-muted">Last contact: {formatShortDate(m.lastContactAt)}</div>
                  <div className="small mt-2">{m.conversationSummary}</div>
                  <div className="small text-muted mt-1">Context: {m.campaignOrTask}</div>
                </Card.Body>
              </Card>
            ))
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <TaskDetailOffcanvas
        task={taskDetail}
        assistant={taskDetail ? assistantById.get(taskDetail.assistantId) : undefined}
        onHide={() => setTaskDetail(null)}
        onEdit={(t) => {
          setTaskBeingEdited(t);
          setTaskDetail(null);
        }}
        deleting={deletingTask}
        onDelete={async (t: ExecutiveTask) => {
          if (!tenantId) return;
          if (!window.confirm(`Delete "${t.name}"? It will be removed from your task list.`)) return;
          try {
            await deleteTaskMut({ id: t.id, tenantId }).unwrap();
            setTaskDetail(null);
            toast.success('Task deleted.');
            await logActivity(`Deleted task "${t.name}"`);
          } catch (e: unknown) {
            toast.error(eaRtkErrorDetail(e, 'Could not delete task'));
          }
        }}
      />

      <ScheduledEmailEditModal
        show={scheduledEmailBeingEdited != null}
        email={scheduledEmailBeingEdited}
        assistants={assistants}
        tenantId={tenantId}
        onHide={() => setScheduledEmailBeingEdited(null)}
        onSave={async (id, patch) => {
          await patchScheduledEmail(id, patch);
          setScheduledEmailBeingEdited(null);
          toast.success('Scheduled email updated.');
        }}
      />

      <ScheduledEmailDetailOffcanvas
        email={scheduledEmailDetail}
        assistant={scheduledEmailDetail ? assistantById.get(scheduledEmailDetail.assistantId) : undefined}
        onHide={() => setScheduledEmailDetail(null)}
        onPatch={patchScheduledEmail}
        onLogActivity={logActivity}
        onOpenEdit={() => {
          if (scheduledEmailDetail) setScheduledEmailBeingEdited({ ...scheduledEmailDetail });
        }}
      />
    </div>
  );
};

function AlertInline({ message }: { message: string }) {
  return (
    <div className="rounded-3 border bg-white p-4 text-muted small shadow-sm">
      {message}
    </div>
  );
}

const TONE_STYLE_EXAMPLES: { title: string; text: string }[] = [
  {
    title: 'Default professional (B2B)',
    text: 'Professional, clear, and concise. Friendly but not casual. No slang or exclamation marks unless the thread already uses them. Prefer short paragraphs and one clear ask per message.',
  },
  {
    title: 'Warm / relationship',
    text: 'Warm and human, conversational but still respectful. Use "we" when it fits. Acknowledge the other person\'s time. Avoid sounding salesy or robotic.',
  },
  {
    title: 'Direct / ops',
    text: 'Very direct and efficient. Lead with the decision or ask. Minimal pleasantries. Bullet lists OK when it saves time.',
  },
  {
    title: 'Executive / board tone',
    text: 'Executive-level: confident, neutral, and precise. No hedging unless uncertainty is real. Prefer implications and next steps over long background.',
  },
  {
    title: 'Creative / brand voice (if you have one)',
    text: 'Match our brand: [2–3 adjectives, e.g. bold, witty, inclusive]. Avoid [things you hate: e.g. clichés, "hope this finds you well," all-caps, emojis].',
  },
];

function ToneWritingStyleHelpOffcanvas({
  show,
  onHide,
  onInsert,
}: {
  show: boolean;
  onHide: () => void;
  onInsert: (text: string) => void;
}) {
  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      scroll
      backdropClassName="ea-tone-help-backdrop"
      className="ea-tone-help-offcanvas"
      style={{ width: 'min(440px, 100vw)' }}
      enforceFocus={false}
    >
      <Offcanvas.Header closeButton className="border-bottom">
        <Offcanvas.Title className="d-flex align-items-center gap-2">
          <InfoCircle className="text-primary flex-shrink-0" size={20} aria-hidden />
          <span className="fw-bold">Tone & writing style</span>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="small">
        <p className="text-muted mb-3">
          Short examples you can paste or tweak. Use <strong>Insert</strong> to replace the current field, or copy and edit.
        </p>
        {TONE_STYLE_EXAMPLES.map(({ title, text }) => (
          <div key={title} className="mb-4 pb-3 border-bottom">
            <h6 className="fw-semibold mb-2">{title}</h6>
            <p className="mb-2 text-body-secondary" style={{ whiteSpace: 'pre-wrap' }}>
              {text}
            </p>
            <Button size="sm" variant="outline-primary" onClick={() => onInsert(text)}>
              Insert into field
            </Button>
          </div>
        ))}
        <h6 className="fw-semibold mb-2">What usually works well</h6>
        <ul className="ps-3 mb-0 text-body-secondary">
          <li className="mb-2">Audience (who reads it: client, vendor, internal team).</li>
          <li className="mb-2">Formality (e.g. &quot;Dear&quot; vs &quot;Hi&quot; — or &quot;mirror the thread&quot;).</li>
          <li className="mb-2">Length (e.g. &quot;under 150 words unless they asked for detail&quot;).</li>
          <li className="mb-2">Taboos (no guilt, no fake urgency, no &quot;just circling back&quot; unless you want that).</li>
          <li>Mirroring: Match the tone of the last message in the thread when reasonable.</li>
        </ul>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

function AssistantEditModal({
  show,
  onHide,
  initial,
  onSave,
}: {
  show: boolean;
  onHide: () => void;
  initial: ExecutiveAssistantProfile | null;
  onSave: (a: ExecutiveAssistantProfile) => void | Promise<void>;
}) {
  const [form, setForm] = useState<ExecutiveAssistantProfile | null>(null);
  const [toneHelpOpen, setToneHelpOpen] = useState(false);
  useEffect(() => {
    if (initial) setForm({ ...initial });
  }, [initial]);
  useEffect(() => {
    if (!show) setToneHelpOpen(false);
  }, [show]);

  if (!form) return null;

  const setField = <K extends keyof ExecutiveAssistantProfile>(k: K, v: ExecutiveAssistantProfile[K]) => {
    setForm((f) => (f ? { ...f, [k]: v } : f));
  };

  const onPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !form) return;
    const reader = new FileReader();
    reader.onload = () => setField('photoUrl', String(reader.result || ''));
    reader.readAsDataURL(file);
  };

  const isNew = !form.id;

  return (
    <>
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{isNew ? 'New assistant' : 'Edit assistant'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>First name</Form.Label>
              <Form.Control value={form.firstName} onChange={(e) => setField('firstName', e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Last name</Form.Label>
              <Form.Control value={form.lastName} onChange={(e) => setField('lastName', e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Title / role</Form.Label>
              <Form.Control placeholder="e.g. Executive Assistant to Phil Kross" value={form.title} onChange={(e) => setField('title', e.target.value)} />
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Group>
              <Form.Label>Profile photo</Form.Label>
              <Form.Control type="file" accept="image/*" onChange={onPhoto} />
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Group>
              <Form.Label>Signature block</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.signature} onChange={(e) => setField('signature', e.target.value)} />
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Group>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
                <Form.Label className="mb-0">Tone / writing style</Form.Label>
                <Button type="button" variant="link" size="sm" className="p-0" onClick={() => setToneHelpOpen(true)}>
                  Examples & tips
                </Button>
              </div>
              <Form.Control as="textarea" rows={2} value={form.toneProfile} onChange={(e) => setField('toneProfile', e.target.value)} />
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Group>
              <Form.Label>Intro rules</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.introRules} onChange={(e) => setField('introRules', e.target.value)} />
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Check
              type="switch"
              id="ea-skip-reintro"
              label="Do not re-introduce if a prior conversation exists"
              checked={form.skipReintroIfKnown}
              onChange={(e) => setField('skipReintroIfKnown', e.target.checked)}
            />
          </Col>
          <Col xs={12}>
            <Form.Check
              type="switch"
              id="ea-active"
              label="Assistant active"
              checked={form.isActive}
              onChange={(e) => setField('isActive', e.target.checked)}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
              toast.error('First name, last name, and email are required.');
              return;
            }
            void onSave({ ...form, lastActivityAt: form.lastActivityAt || new Date().toISOString() });
          }}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
    <ToneWritingStyleHelpOffcanvas
      show={toneHelpOpen}
      onHide={() => setToneHelpOpen(false)}
      onInsert={(text) => {
        setField('toneProfile', text);
        toast.success('Inserted into Tone / writing style.');
      }}
    />
    </>
  );
}

function padTimePart(n: number): string {
  return String(n).padStart(2, '0');
}

function nextActionToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${padTimePart(d.getMonth() + 1)}-${padTimePart(d.getDate())}T${padTimePart(d.getHours())}:${padTimePart(
    d.getMinutes()
  )}`;
}

function datetimeLocalToIso(local: string): string | null {
  const s = String(local || '').trim();
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function TaskEditModal({
  show,
  task,
  assistants,
  tenantId,
  onHide,
  onSave,
}: {
  show: boolean;
  task: ExecutiveTask | null;
  assistants: ExecutiveAssistantProfile[];
  tenantId: string;
  onHide: () => void;
  onSave: (id: string, body: Record<string, unknown>) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [assistantId, setAssistantId] = useState('');
  const [type, setType] = useState<TaskType>('monitoring');
  const [objective, setObjective] = useState('');
  const [scope, setScope] = useState<TaskScope>('private');
  const [campaignMode, setCampaignMode] = useState<CampaignMode>('none');
  const [linkCampaignId, setLinkCampaignId] = useState('');
  const [newCampaignName, setNewCampaignName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [clientId, setClientId] = useState('');
  const [contactId, setContactId] = useState('');
  const [status, setStatus] = useState<TaskStatus>('draft');
  const [nextActionLocal, setNextActionLocal] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show || !task) return;
    setName(task.name);
    setAssistantId(task.assistantId);
    setType(task.type);
    setObjective(task.objective === '—' ? '' : task.objective);
    setScope(task.scope);
    setCampaignMode(task.campaignMode);
    setLinkCampaignId(task.linkedCampaignId || '');
    setNewCampaignName('');
    setAccountId(task.accountId || '');
    setClientId(task.clientId || '');
    setContactId(task.contactId || '');
    setStatus(task.status);
    setNextActionLocal(nextActionToDatetimeLocal(task.nextActionAt));
  }, [show, task]);

  useEffect(() => {
    if (campaignMode === 'none') {
      setLinkCampaignId('');
      setNewCampaignName('');
    }
  }, [campaignMode]);

  const submit = async () => {
    if (!task || !tenantId) return;
    if (!name.trim() || !assistantId) {
      toast.error('Name and assistant are required.');
      return;
    }
    if (campaignMode === 'link' && !linkCampaignId.trim()) {
      toast.error('Campaign ID is required when linking an existing campaign.');
      return;
    }
    if (campaignMode === 'create' && !newCampaignName.trim()) {
      toast.error('New campaign name is required when creating a campaign.');
      return;
    }
    const nextIso = datetimeLocalToIso(nextActionLocal);
    const body: Record<string, unknown> = {
      tenantId,
      name: name.trim(),
      assignedAssistantId: assistantId,
      taskType: type,
      objective: objective.trim() || '—',
      status,
      scope,
      campaignMode,
      nextActionAt: nextIso,
      accountId: accountId.trim() || null,
      clientId: clientId.trim() || null,
      contactId: contactId.trim() || null,
    };
    if (campaignMode === 'link') body.campaignId = linkCampaignId.trim();
    if (campaignMode === 'create') body.newCampaignName = newCampaignName.trim();

    setSaving(true);
    try {
      await onSave(task.id, body);
      toast.success('Task updated.');
    } catch (e: unknown) {
      toast.error(eaRtkErrorDetail(e, 'Could not update task'));
    } finally {
      setSaving(false);
    }
  };

  if (!task) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Edit task</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">
            Task name <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control size="sm" value={name} onChange={(e) => setName(e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">
            Assigned assistant <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select size="sm" value={assistantId} onChange={(e) => setAssistantId(e.target.value)}>
            {assistants.map((a) => (
              <option key={a.id} value={a.id}>
                {a.firstName} {a.lastName}
                {a.email ? ` <${a.email}>` : ''}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Row className="g-2 mb-2">
          <Col xs={12} sm={6}>
            <Form.Group>
              <Form.Label className="small fw-semibold">Task type</Form.Label>
              <Form.Select size="sm" value={type} onChange={(e) => setType(e.target.value as TaskType)}>
                <option value="monitoring">Monitoring</option>
                <option value="campaign_linked">Campaign-linked</option>
                <option value="one_time_email">One-time scheduled email</option>
                <option value="monitoring_only">Monitoring only</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Group>
              <Form.Label className="small fw-semibold">Visibility scope</Form.Label>
              <Form.Select size="sm" value={scope} onChange={(e) => setScope(e.target.value as TaskScope)}>
                <option value="private">Private (you and admins)</option>
                <option value="global">Global (tenant-visible)</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">Objective / messaging</Form.Label>
          <Form.Control as="textarea" rows={3} size="sm" value={objective} onChange={(e) => setObjective(e.target.value)} />
        </Form.Group>
        <Row className="g-2 mb-2">
          <Col xs={12} sm={6}>
            <Form.Group>
              <Form.Label className="small fw-semibold">Status</Form.Label>
              <Form.Select size="sm" value={status} onChange={(e) => setStatus(e.target.value as TaskStatus)}>
                {(Object.keys(TASK_STATUS_LABEL) as TaskStatus[]).map((k) => (
                  <option key={k} value={k}>
                    {TASK_STATUS_LABEL[k]}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Group>
              <Form.Label className="small fw-semibold">Next action</Form.Label>
              <Form.Control
                type="datetime-local"
                size="sm"
                value={nextActionLocal}
                onChange={(e) => setNextActionLocal(e.target.value)}
              />
              <Form.Text className="text-muted">{"Uses your browser's local time zone."}</Form.Text>
            </Form.Group>
          </Col>
        </Row>
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">Campaign mode</Form.Label>
          <Form.Select size="sm" value={campaignMode} onChange={(e) => setCampaignMode(e.target.value as CampaignMode)}>
            <option value="none">No campaign</option>
            <option value="link">Link existing campaign</option>
            <option value="create">Create new campaign (stub)</option>
          </Form.Select>
        </Form.Group>
        {campaignMode === 'link' ? (
          <Form.Group className="mb-2">
            <Form.Label className="small fw-semibold">
              Campaign ID <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control size="sm" value={linkCampaignId} onChange={(e) => setLinkCampaignId(e.target.value)} placeholder="Existing campaign row id" />
          </Form.Group>
        ) : null}
        {campaignMode === 'create' ? (
          <Form.Group className="mb-2">
            <Form.Label className="small fw-semibold">
              New campaign name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control size="sm" value={newCampaignName} onChange={(e) => setNewCampaignName(e.target.value)} />
          </Form.Group>
        ) : null}
        <div className="fw-semibold small mb-2">Optional CRM links</div>
        <Row className="g-2 mb-2">
          <Col xs={12} sm={4}>
            <Form.Group>
              <Form.Label className="small text-muted">Account ID</Form.Label>
              <Form.Control size="sm" value={accountId} onChange={(e) => setAccountId(e.target.value)} />
            </Form.Group>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Group>
              <Form.Label className="small text-muted">Client ID</Form.Label>
              <Form.Control size="sm" value={clientId} onChange={(e) => setClientId(e.target.value)} />
            </Form.Group>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Group>
              <Form.Label className="small text-muted">Contact ID</Form.Label>
              <Form.Control size="sm" value={contactId} onChange={(e) => setContactId(e.target.value)} />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" disabled={saving || !assistants.length} onClick={() => void submit()}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving…
            </>
          ) : (
            'Save changes'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function TaskWizardModal({
  show,
  onHide,
  assistants,
  tenantId,
  onCreate,
}: {
  show: boolean;
  onHide: () => void;
  assistants: ExecutiveAssistantProfile[];
  tenantId: string;
  onCreate: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [assistantId, setAssistantId] = useState('');
  const [type, setType] = useState<TaskType>('monitoring');
  const [objective, setObjective] = useState('');
  const [audience, setAudience] = useState('non_clients');
  const [renewalDays, setRenewalDays] = useState('30');
  const [scope, setScope] = useState<TaskScope>('private');
  const [campaignMode, setCampaignMode] = useState<CampaignMode>('none');
  const [linkCampaignId, setLinkCampaignId] = useState('');
  const [newCampaignName, setNewCampaignName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [clientId, setClientId] = useState('');
  const [contactId, setContactId] = useState('');
  const [stopReply, setStopReply] = useState(true);
  const [stopClient, setStopClient] = useState(true);
  const [status, setStatus] = useState<TaskStatus>('draft');

  useEffect(() => {
    if (show && assistants.length && !assistantId) setAssistantId(assistants[0].id);
  }, [show, assistants, assistantId]);

  useEffect(() => {
    if (campaignMode === 'none') {
      setLinkCampaignId('');
      setNewCampaignName('');
    }
  }, [campaignMode]);

  const submit = async () => {
    if (!name.trim() || !assistantId || !tenantId) {
      toast.error('Name and assistant are required.');
      return;
    }
    if (campaignMode === 'link' && !linkCampaignId.trim()) {
      toast.error('Campaign ID is required when linking an existing campaign.');
      return;
    }
    if (campaignMode === 'create' && !newCampaignName.trim()) {
      toast.error('New campaign name is required when creating a campaign.');
      return;
    }

    const payload: Record<string, unknown> = {
      tenantId,
      name: name.trim(),
      assignedAssistantId: assistantId,
      taskType: type,
      objective: objective.trim() || '—',
      status,
      priority: 'normal',
      scope,
      campaignMode,
      audience,
      renewalDays: Number(renewalDays) || 30,
      triggerRules: { stopOnReply: stopReply, stopOnClient: stopClient },
      nextActionAt: new Date(Date.now() + 86400_000).toISOString(),
      contactsLabel: '0 contacts',
      lastStep: 'Created from Executive Assistant',
      progress: defaultProgress(),
    };

    if (campaignMode === 'link') payload.campaignId = linkCampaignId.trim();
    if (campaignMode === 'create') payload.newCampaignName = newCampaignName.trim();
    if (accountId.trim()) payload.accountId = accountId.trim();
    if (clientId.trim()) payload.clientId = clientId.trim();
    if (contactId.trim()) payload.contactId = contactId.trim();

    await onCreate(payload);
    setName('');
    setObjective('');
    setLinkCampaignId('');
    setNewCampaignName('');
    setAccountId('');
    setClientId('');
    setContactId('');
    setCampaignMode('none');
    setScope('private');
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Create task</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Accordion alwaysOpen defaultActiveKey={['0', '1', '2', '3', '4', '5']}>
          <Accordion.Item eventKey="0">
            <Accordion.Header>Step 1 — Basic info</Accordion.Header>
            <Accordion.Body>
              <Form.Group className="mb-2">
                <Form.Label>
                  Task name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Monitor contract renewals" />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>
                  Assigned assistant <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select value={assistantId} onChange={(e) => setAssistantId(e.target.value)}>
                  {assistants.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.firstName} {a.lastName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Task type</Form.Label>
                <Form.Select value={type} onChange={(e) => setType(e.target.value as TaskType)}>
                  <option value="monitoring">Monitoring (e.g. renewal dates)</option>
                  <option value="campaign_linked">Campaign-linked</option>
                  <option value="one_time_email">One-time scheduled email</option>
                  <option value="monitoring_only">Monitoring only</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Visibility scope</Form.Label>
                <Form.Select value={scope} onChange={(e) => setScope(e.target.value as TaskScope)}>
                  <option value="private">Private (you and admins)</option>
                  <option value="global">Global (tenant-visible)</option>
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Objective</Form.Label>
                <Form.Control as="textarea" rows={2} value={objective} onChange={(e) => setObjective(e.target.value)} />
              </Form.Group>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="1">
            <Accordion.Header>Step 2 — Audience / trigger</Accordion.Header>
            <Accordion.Body>
              <Form.Group className="mb-2">
                <Form.Label>Audience</Form.Label>
                <Form.Select value={audience} onChange={(e) => setAudience(e.target.value)}>
                  <option value="current_clients">Current clients</option>
                  <option value="non_clients">Non-clients</option>
                  <option value="leads">Leads</option>
                  <option value="custom">Custom filter (CRM)</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Start outreach N days before renewal</Form.Label>
                <Form.Control type="number" min={1} value={renewalDays} onChange={(e) => setRenewalDays(e.target.value)} />
              </Form.Group>
              <p className="small text-muted mb-0">Full CRM filters and tag logic will connect here.</p>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="2">
            <Accordion.Header>Step 3 — Campaign (explicit)</Accordion.Header>
            <Accordion.Body>
              <Form.Group className="mb-2">
                <Form.Label>Campaign mode</Form.Label>
                <Form.Select value={campaignMode} onChange={(e) => setCampaignMode(e.target.value as CampaignMode)}>
                  <option value="none">No campaign</option>
                  <option value="link">Link existing campaign</option>
                  <option value="create">Create new campaign (stub record)</option>
                </Form.Select>
              </Form.Group>
              {campaignMode === 'link' ? (
                <Form.Group className="mb-2">
                  <Form.Label>
                    Existing campaign ID <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control value={linkCampaignId} onChange={(e) => setLinkCampaignId(e.target.value)} placeholder="Campaign row id from DynamoDB / Campaigns" />
                </Form.Group>
              ) : null}
              {campaignMode === 'create' ? (
                <Form.Group className="mb-2">
                  <Form.Label>
                    New campaign name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control value={newCampaignName} onChange={(e) => setNewCampaignName(e.target.value)} placeholder="Name for the stub campaign record" />
                </Form.Group>
              ) : null}
              <p className="small text-muted mb-0">The API does not attach a campaign unless you choose link or create.</p>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="5">
            <Accordion.Header>Optional links (CRM)</Accordion.Header>
            <Accordion.Body>
              <Form.Group className="mb-2">
                <Form.Label>Account ID</Form.Label>
                <Form.Control value={accountId} onChange={(e) => setAccountId(e.target.value)} placeholder="Optional" />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Client ID</Form.Label>
                <Form.Control value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="Optional" />
              </Form.Group>
              <Form.Group>
                <Form.Label>Contact ID</Form.Label>
                <Form.Control value={contactId} onChange={(e) => setContactId(e.target.value)} placeholder="Optional" />
              </Form.Group>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="3">
            <Accordion.Header>Step 4 — Rules</Accordion.Header>
            <Accordion.Body>
              <Form.Check className="mb-2" checked={stopReply} onChange={(e) => setStopReply(e.target.checked)} label="Stop on reply" />
              <Form.Check className="mb-2" checked={stopClient} onChange={(e) => setStopClient(e.target.checked)} label="Stop if contact becomes a client" />
              <Form.Check className="mb-2" label="Notify owner on reply" disabled />
              <Form.Check label="Always CC owner" disabled />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="4">
            <Accordion.Header>Step 5 — Review / activate</Accordion.Header>
            <Accordion.Body>
              <Form.Group>
                <Form.Label>Initial status</Form.Label>
                <Form.Select value={status} onChange={(e) => setStatus(e.target.value as TaskStatus)}>
                  <option value="draft">Save as draft</option>
                  <option value="active">Activate</option>
                </Form.Select>
              </Form.Group>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => void submit()}>
          Save task
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * Searchable typeahead for the optional "Related company" field on scheduled emails.
 * Surfaces names from Accounts, Target Accounts, and Clients' organization tags so users
 * pick from real CRM entities instead of re-typing (e.g. avoiding "Acme" vs "Acme, Inc.").
 * Free-text is still allowed — pure typing without picking a suggestion is fine.
 * Storage shape is unchanged: only the display string is saved on the scheduled-email row.
 */
function CompanyTypeahead({
  value,
  onChange,
  tenantId,
  placeholder = 'Company name',
  disabled = false,
}: {
  value: string;
  onChange: (v: string) => void;
  tenantId: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  const skip = !tenantId;
  const { data: accounts = [] } = useGetAccountsQuery(undefined, { skip });
  const { data: targets = [] } = useGetTargetAccountsQuery(undefined, { skip });
  const { data: clients = [] } = useGetClientsQuery(undefined, { skip });

  const suggestions = useMemo(() => {
    type Src = 'account' | 'target' | 'client_org';
    const seen = new Map<string, { name: string; sources: Set<Src> }>();
    const push = (raw: unknown, src: Src) => {
      const trimmed = String(raw || '').trim();
      if (!trimmed) return;
      const key = trimmed.toLowerCase();
      if (!seen.has(key)) seen.set(key, { name: trimmed, sources: new Set() });
      seen.get(key)!.sources.add(src);
    };
    for (const a of accounts as any[]) push(a?.name, 'account');
    for (const t of targets as any[]) push(t?.companyName, 'target');
    for (const c of clients as any[]) push(c?.organization, 'client_org');
    return Array.from(seen.values()).sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }, [accounts, targets, clients]);

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    const base = q
      ? suggestions.filter((s) => s.name.toLowerCase().includes(q))
      : suggestions;
    return base.slice(0, 20);
  }, [suggestions, value]);

  const exact = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return false;
    return suggestions.some((s) => s.name.toLowerCase() === q);
  }, [suggestions, value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="position-relative" ref={containerRef}>
      <Form.Control
        type="text"
        size="sm"
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false);
            (e.target as HTMLInputElement).blur();
          }
        }}
        aria-autocomplete="list"
        aria-expanded={open}
      />
      {open && (filtered.length > 0 || (value.trim() && !exact)) ? (
        <div
          className="shadow-sm border rounded bg-white"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1080,
            maxHeight: 240,
            overflowY: 'auto',
            marginTop: 2,
          }}
          role="listbox"
        >
          {filtered.map((s) => (
            <button
              key={s.name}
              type="button"
              role="option"
              className="d-flex w-100 align-items-center gap-2 px-2 py-1 border-0 bg-transparent text-start"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(s.name);
                setOpen(false);
              }}
              style={{ fontSize: '0.85rem' }}
            >
              <span className="flex-grow-1 text-truncate" title={s.name}>
                {s.name}
              </span>
              <span className="d-flex gap-1 flex-shrink-0">
                {s.sources.has('account') ? (
                  <Badge bg="primary" pill style={{ fontSize: '0.62rem' }}>
                    Account
                  </Badge>
                ) : null}
                {s.sources.has('target') ? (
                  <Badge bg="warning" text="dark" pill style={{ fontSize: '0.62rem' }}>
                    Target
                  </Badge>
                ) : null}
                {s.sources.has('client_org') ? (
                  <Badge bg="secondary" pill style={{ fontSize: '0.62rem' }}>
                    Client
                  </Badge>
                ) : null}
              </span>
            </button>
          ))}
          {value.trim() && !exact ? (
            <div className="px-2 py-1 small text-muted border-top">
              Or keep “<strong>{value.trim()}</strong>” as a new label.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ScheduleEmailModal({
  show,
  onHide,
  assistants,
  tenantId,
  onCreate,
}: {
  show: boolean;
  onHide: () => void;
  assistants: ExecutiveAssistantProfile[];
  tenantId: string;
  onCreate: (body: Record<string, unknown>) => Promise<void>;
}) {
  const [assistantId, setAssistantId] = useState('');
  const [company, setCompany] = useState('');
  const [sendDate, setSendDate] = useState('');
  const [sendTime, setSendTime] = useState('');
  const [sendTimezone, setSendTimezone] = useState(() => getDeviceIanaTimeZoneId());

  const deviceTimeZoneId = useMemo(() => getDeviceIanaTimeZoneId(), []);

  /** Empty field falls back to the device zone so users are not forced to type it every time. */
  const tzEffective = sendTimezone.trim() || deviceTimeZoneId;
  const scheduleSendExplicit = useMemo(() => {
    if (!sendDate.trim() || !sendTime.trim() || !isValidIanaTimeZone(tzEffective)) return null;
    return parseScheduleSendAt(sendDate, sendTime, tzEffective);
  }, [sendDate, sendTime, tzEffective]);

  const scheduleTzShortLabel = useMemo(() => {
    if (!isValidIanaTimeZone(tzEffective)) return '';
    const instant = scheduleSendExplicit ?? new Date();
    return scheduleTimeZoneShort(tzEffective, instant);
  }, [tzEffective, scheduleSendExplicit]);

  const scheduleTzPopoverBody = useMemo(() => {
    if (!isValidIanaTimeZone(tzEffective)) {
      return 'Enter a valid IANA region/city time zone (e.g. America/New_York, Europe/London). Clear the field to use your device zone (' + deviceTimeZoneId + ').';
    }
    const instant = scheduleSendExplicit ?? new Date();
    const sameAsDevice = tzEffective === deviceTimeZoneId;
    if (sameAsDevice) {
      return `Send time uses your device’s local time (${scheduleTimeZoneUserLabel(deviceTimeZoneId, instant)}). You can change the zone above if needed.`;
    }
    return `Send time is interpreted as ${scheduleTimeZoneUserLabel(tzEffective, instant)}. Your device is set to ${scheduleTimeZoneUserLabel(
      deviceTimeZoneId,
      new Date()
    )}.`;
  }, [tzEffective, scheduleSendExplicit, deviceTimeZoneId]);

  const [composeData, setComposeData] = useState<ScheduleComposeData>({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
  });
  const [composeAttachments, setComposeAttachments] = useState<ScheduleComposeAttachment[]>([]);
  const [composeConfidentialMode, setComposeConfidentialMode] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scriptSearchQuery, setScriptSearchQuery] = useState('');
  const [scriptSort, setScriptSort] = useState<EaScriptSortMode>('updated_desc');
  const [scriptFilterCategory, setScriptFilterCategory] = useState('');
  const [scriptFilterStatus, setScriptFilterStatus] = useState('');
  const [scriptFilterTags, setScriptFilterTags] = useState('');
  const [scriptPanelOpen, setScriptPanelOpen] = useState(false);
  // Cap rendered rows so libraries with thousands of scripts don't push the composer off-screen.
  const EA_SCRIPT_LIBRARY_PAGE_SIZE = 25;
  const [scriptVisibleLimit, setScriptVisibleLimit] = useState(EA_SCRIPT_LIBRARY_PAGE_SIZE);
  const [scriptPreview, setScriptPreview] = useState<EaScheduleMessageScript | null>(null);
  const [scriptSaveOpen, setScriptSaveOpen] = useState(false);
  const [scriptSaveTitle, setScriptSaveTitle] = useState('');
  const [scriptSaveCategory, setScriptSaveCategory] = useState('');
  const [scriptSaveTags, setScriptSaveTags] = useState('');
  const [scriptSaveUseCase, setScriptSaveUseCase] = useState('');
  const scheduleBodyRef = useRef<HTMLTextAreaElement | null>(null);
  const scheduleAttachmentInputRef = useRef<HTMLInputElement | null>(null);

  const skipScripts = !show || !tenantId;
  const {
    data: messageScripts = [],
    isLoading: loadingMessageScripts,
    isError: messageScriptsError,
    error: messageScriptsErr,
  } = useGetEaScheduleMessageScriptsQuery({ tenantId }, { skip: skipScripts });
  const [createEaScheduleMessageScript, { isLoading: savingScript }] = useCreateEaScheduleMessageScriptMutation();
  const [updateEaScheduleMessageScript] = useUpdateEaScheduleMessageScriptMutation();
  const [recordEaScheduleMessageScriptUse] = useRecordEaScheduleMessageScriptUseMutation();
  const [deleteEaScheduleMessageScript] = useDeleteEaScheduleMessageScriptMutation();

  useEffect(() => {
    if (show && tenantId) {
      setScriptSearchQuery('');
      setScriptSort('updated_desc');
      setScriptFilterCategory('');
      setScriptFilterStatus('');
      setScriptFilterTags('');
      setScriptPreview(null);
      setScriptSaveOpen(false);
      setScriptPanelOpen(false);
      setScriptVisibleLimit(EA_SCRIPT_LIBRARY_PAGE_SIZE);
    }
  }, [show, tenantId]);

  const hasActiveScriptFilter = Boolean(
    scriptSearchQuery.trim()
    || scriptFilterCategory
    || scriptFilterStatus
    || scriptFilterTags.trim()
  );

  // Auto-open the library as soon as the user starts narrowing scope; never auto-close
  // (clearing filters leaves it open so the user can still see results).
  useEffect(() => {
    if (hasActiveScriptFilter) setScriptPanelOpen(true);
  }, [hasActiveScriptFilter]);

  // Reset pagination whenever the underlying list of matches changes.
  useEffect(() => {
    setScriptVisibleLimit(EA_SCRIPT_LIBRARY_PAGE_SIZE);
  }, [scriptSearchQuery, scriptSort, scriptFilterCategory, scriptFilterStatus, scriptFilterTags]);

  const scriptCategoryOptions = useMemo(() => {
    const known = new Set<string>(EA_SCRIPT_LIBRARY_CATEGORIES);
    const extras = new Set<string>();
    for (const s of messageScripts) {
      const c = String(s.category || '').trim();
      if (c && !known.has(c)) extras.add(c);
    }
    const merged = [...EA_SCRIPT_LIBRARY_CATEGORIES, ...Array.from(extras)];
    merged.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return merged;
  }, [messageScripts]);

  const scriptLibraryTagSuggestions = useMemo(() => {
    const seen = new Set<string>();
    for (const s of messageScripts) {
      for (const t of s.tags || []) {
        const x = String(t).trim().toLowerCase();
        if (x) seen.add(x);
      }
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [messageScripts]);

  const filteredSortedScripts = useMemo(
    () =>
      filterAndSortEaScheduleScripts(messageScripts, scriptSearchQuery, scriptSort, {
        category: scriptFilterCategory,
        status: scriptFilterStatus,
        tagsFilter: scriptFilterTags,
      }),
    [messageScripts, scriptSearchQuery, scriptSort, scriptFilterCategory, scriptFilterStatus, scriptFilterTags]
  );

  const handleComposeChange = (field: keyof ScheduleComposeData, value: string) => {
    setComposeData((prev) => ({ ...prev, [field]: value }));
    if (field === 'subject') {
      setComposeConfidentialMode(
        String(value || '')
          .trimStart()
          .toLowerCase()
          .startsWith(EA_SCHEDULE_COMPOSE_CONFIDENTIAL_PREFIX.toLowerCase())
      );
    }
  };

  const updateBodySelection = (nextBody: string, selectionStart: number, selectionEnd: number = selectionStart) => {
    handleComposeChange('body', nextBody);
    window.requestAnimationFrame(() => {
      const el = scheduleBodyRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(selectionStart, selectionEnd);
    });
  };

  const wrapSelection = (prefix: string, suffix: string, placeholder: string) => {
    const bodyInput = scheduleBodyRef.current;
    const currentBody = String(composeData.body || '');
    if (!bodyInput) {
      handleComposeChange('body', `${currentBody}${prefix}${placeholder}${suffix}`);
      return;
    }
    const selectionStart = bodyInput.selectionStart ?? currentBody.length;
    const selectionEnd = bodyInput.selectionEnd ?? selectionStart;
    const selectedText = currentBody.slice(selectionStart, selectionEnd);
    const textToWrap = selectedText || placeholder;
    const replacement = `${prefix}${textToWrap}${suffix}`;
    const nextBody = `${currentBody.slice(0, selectionStart)}${replacement}${currentBody.slice(selectionEnd)}`;
    const nextSelectionStart = selectedText ? selectionStart + replacement.length : selectionStart + prefix.length;
    const nextSelectionEnd = selectedText ? nextSelectionStart : nextSelectionStart + textToWrap.length;
    updateBodySelection(nextBody, nextSelectionStart, nextSelectionEnd);
  };

  const insertAtCursor = (insertText: string) => {
    const bodyInput = scheduleBodyRef.current;
    const currentBody = String(composeData.body || '');
    if (!bodyInput) {
      handleComposeChange('body', `${currentBody}${insertText}`);
      return;
    }
    const selectionStart = bodyInput.selectionStart ?? currentBody.length;
    const selectionEnd = bodyInput.selectionEnd ?? selectionStart;
    const nextBody = `${currentBody.slice(0, selectionStart)}${insertText}${currentBody.slice(selectionEnd)}`;
    const nextCursor = selectionStart + insertText.length;
    updateBodySelection(nextBody, nextCursor);
  };

  const openAttachmentPicker = (accept: string) => {
    if (!scheduleAttachmentInputRef.current) return;
    scheduleAttachmentInputRef.current.accept = accept;
    scheduleAttachmentInputRef.current.value = '';
    scheduleAttachmentInputRef.current.click();
  };

  const handleAttachmentInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (selectedFiles.length === 0) return;

    const existingKeys = new Set(composeAttachments.map((a) => `${a.name}:${a.size}:${a.lastModified}`));
    const existingTotal = composeAttachments.reduce((s, a) => s + Number(a.size || 0), 0);
    const skipped: string[] = [];
    const accepted: File[] = [];
    let acceptedTotal = 0;

    for (const file of selectedFiles) {
      const key = `${file.name}:${file.size}:${file.lastModified}`;
      if (existingKeys.has(key)) {
        skipped.push(`${file.name} (already attached)`);
        continue;
      }
      if (file.size > EA_MAX_SCHEDULE_ATTACHMENT_BYTES) {
        skipped.push(`${file.name} (too large; max ${formatScheduleAttachmentSize(EA_MAX_SCHEDULE_ATTACHMENT_BYTES)})`);
        continue;
      }
      if (composeAttachments.length + accepted.length >= EA_MAX_SCHEDULE_ATTACHMENTS) {
        skipped.push(`${file.name} (max ${EA_MAX_SCHEDULE_ATTACHMENTS} attachments)`);
        continue;
      }
      if (existingTotal + acceptedTotal + file.size > EA_MAX_SCHEDULE_ATTACHMENTS_TOTAL_BYTES) {
        skipped.push(
          `${file.name} (total attachment size limit ${formatScheduleAttachmentSize(EA_MAX_SCHEDULE_ATTACHMENTS_TOTAL_BYTES)})`
        );
        continue;
      }
      existingKeys.add(key);
      accepted.push(file);
      acceptedTotal += file.size;
    }

    const nextAttachments: ScheduleComposeAttachment[] = [];
    for (const file of accepted) {
      try {
        const contentBase64 = await readScheduleFileAsBase64(file);
        nextAttachments.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Date.now()}`,
          name: file.name,
          contentType: file.type || 'application/octet-stream',
          size: file.size,
          contentBase64,
          lastModified: file.lastModified,
        });
      } catch {
        skipped.push(`${file.name} (failed to read file)`);
      }
    }
    if (nextAttachments.length > 0) setComposeAttachments((prev) => [...prev, ...nextAttachments]);
    if (skipped.length > 0) window.alert(`Some files were skipped:\n- ${skipped.join('\n- ')}`);
  };

  const handleInsertLink = () => {
    const bodyInput = scheduleBodyRef.current;
    const currentBody = String(composeData.body || '');
    const selectionStart = bodyInput?.selectionStart ?? currentBody.length;
    const selectionEnd = bodyInput?.selectionEnd ?? selectionStart;
    const selectedText = currentBody.slice(selectionStart, selectionEnd);
    const rawUrl = window.prompt('Enter URL', 'https://');
    if (!rawUrl) return;
    const normalizedUrl = /^https?:\/\//i.test(rawUrl) ? rawUrl.trim() : `https://${rawUrl.trim()}`;
    if (!normalizedUrl || normalizedUrl === 'https://') return;
    const linkText =
      selectedText || window.prompt('Enter link text', normalizedUrl)?.trim() || normalizedUrl;
    const markdownLink = `[${linkText}](${normalizedUrl})`;
    const nextBody = `${currentBody.slice(0, selectionStart)}${markdownLink}${currentBody.slice(selectionEnd)}`;
    updateBodySelection(nextBody, selectionStart + markdownLink.length);
  };

  const handleInsertEmoji = () => {
    const emoji = window.prompt('Enter emoji to insert', '🙂');
    if (!emoji) return;
    insertAtCursor(`${emoji} `);
  };

  const handleToggleConfidential = () => {
    setComposeConfidentialMode((prev) => {
      const next = !prev;
      setComposeData((current) => {
        const currentSubject = String(current.subject || '');
        const hasPrefix = currentSubject
          .trimStart()
          .toLowerCase()
          .startsWith(EA_SCHEDULE_COMPOSE_CONFIDENTIAL_PREFIX.toLowerCase());
        if (next && !hasPrefix) {
          const t = currentSubject.trim();
          return {
            ...current,
            subject: t ? `${EA_SCHEDULE_COMPOSE_CONFIDENTIAL_PREFIX} ${t}` : EA_SCHEDULE_COMPOSE_CONFIDENTIAL_PREFIX,
          };
        }
        if (!next && hasPrefix) {
          return { ...current, subject: currentSubject.replace(/^\s*\[confidential\]\s*/i, '') };
        }
        return current;
      });
      return next;
    });
  };

  const handleInsertScheduleNote = () => {
    const defaultDate = new Date(Date.now() + 60 * 60 * 1000);
    const defaultValue = `${defaultDate.getFullYear()}-${String(defaultDate.getMonth() + 1).padStart(2, '0')}-${String(defaultDate.getDate()).padStart(2, '0')} ${String(defaultDate.getHours()).padStart(2, '0')}:${String(defaultDate.getMinutes()).padStart(2, '0')}`;
    const scheduleText = window.prompt('Add a schedule note (local date/time)', defaultValue);
    if (!scheduleText) return;
    insertAtCursor(`\n[Schedule: ${scheduleText.trim()}]\n`);
  };

  const handleClearDraft = () => {
    const has =
      Boolean(composeData.to || composeData.cc || composeData.bcc || composeData.subject || composeData.body) ||
      composeAttachments.length > 0;
    if (!has) return;
    if (!window.confirm('Clear this draft and remove all attachments?')) return;
    setComposeAttachments([]);
    if (scheduleAttachmentInputRef.current) scheduleAttachmentInputRef.current.value = '';
    setComposeConfidentialMode(false);
    setComposeData({ to: '', cc: '', bcc: '', subject: '', body: '' });
    setCompany('');
    setSendDate('');
    setSendTime('');
    setSendTimezone(deviceTimeZoneId);
  };

  useEffect(() => {
    if (!show) return;
    if (assistants.length) setAssistantId(assistants[0].id);
    setSendDate('');
    setSendTime('');
    setSendTimezone(deviceTimeZoneId);
    setComposeData({ to: '', cc: '', bcc: '', subject: '', body: '' });
    setCompany('');
    setComposeAttachments([]);
    setComposeConfidentialMode(false);
    if (scheduleAttachmentInputRef.current) scheduleAttachmentInputRef.current.value = '';
  }, [show, assistants, deviceTimeZoneId]);

  const submit = async () => {
    if (!composeData.to.trim() || !composeData.subject.trim() || !tenantId) {
      toast.error('To and subject are required.');
      return;
    }
    if (!sendDate.trim() || !sendTime.trim()) {
      toast.error('Choose a send date and time.');
      return;
    }
    const tz = sendTimezone.trim() || deviceTimeZoneId;
    if (!isValidIanaTimeZone(tz)) {
      toast.error('Enter a valid send time zone, or clear the field to use your device zone.');
      return;
    }
    const sendAtDate = parseScheduleSendAt(sendDate, sendTime, tz);
    if (!sendAtDate) {
      toast.error('Invalid send date or time for this time zone (e.g. non-existent clock time at DST change).');
      return;
    }
    if (composeAttachments.length > 0) {
      toast.error('Remove attachments to schedule — scheduled emails do not support attachments yet.');
      return;
    }
    const iso = sendAtDate.toISOString();
    const scriptCtx = buildEaScheduleScriptContext(assistants, assistantId, composeData.to);
    let bodyOut = composeData.body.trim();
    if (composeData.cc.trim() || composeData.bcc.trim()) {
      const lines: string[] = [];
      if (composeData.cc.trim()) lines.push(`Cc: ${composeData.cc.trim()}`);
      if (composeData.bcc.trim()) lines.push(`Bcc: ${composeData.bcc.trim()}`);
      bodyOut = lines.join('\n') + (bodyOut ? `\n\n${bodyOut}` : '');
    }
    const subjectOut = applyScriptPlaceholders(composeData.subject.trim(), scriptCtx);
    bodyOut = applyScriptPlaceholders(bodyOut, scriptCtx);
    setScheduling(true);
    try {
      await onCreate({
        tenantId,
        assistantId,
        recipient: composeData.to.trim(),
        company: company.trim() || '—',
        subject: subjectOut,
        body: bodyOut,
        sendAt: iso,
        sendTimezone: tz,
        status: 'scheduled',
        replyStatus: '—',
      });
      setComposeData({ to: '', cc: '', bcc: '', subject: '', body: '' });
      setCompany('');
      setComposeAttachments([]);
      setComposeConfidentialMode(false);
      if (scheduleAttachmentInputRef.current) scheduleAttachmentInputRef.current.value = '';
    } finally {
      setScheduling(false);
    }
  };

  const applyMessageScriptToComposer = (s: EaScheduleMessageScript) => {
    const ctx = buildEaScheduleScriptContext(assistants, assistantId, composeData.to);
    handleComposeChange('subject', applyScriptPlaceholders(s.subject, ctx));
    handleComposeChange('body', applyScriptPlaceholders(s.body, ctx));
    toast.success(`Loaded “${s.title}”.`);
  };

  const loadScriptIntoComposer = async (s: EaScheduleMessageScript) => {
    applyMessageScriptToComposer(s);
    setScriptPreview(null);
    try {
      await recordEaScheduleMessageScriptUse({ id: s.id, tenantId }).unwrap();
    } catch {
      /* use-count is best-effort */
    }
  };

  const setScriptLibraryStatus = async (s: EaScheduleMessageScript, status: EaScriptLibraryStatus) => {
    try {
      await updateEaScheduleMessageScript({ id: s.id, tenantId, status }).unwrap();
      toast.success(status === 'archived' ? 'Script archived.' : 'Script updated.');
    } catch (e: unknown) {
      toast.error(eaRtkErrorDetail(e, 'Could not update script'));
    }
  };

  const removeScriptFromLibrary = async (s: EaScheduleMessageScript) => {
    if (!window.confirm(`Delete “${s.title}” from your library?`)) return;
    try {
      await deleteEaScheduleMessageScript({ id: s.id, tenantId }).unwrap();
      toast.success('Script deleted.');
      setScriptPreview(null);
    } catch (e: unknown) {
      toast.error(eaRtkErrorDetail(e, 'Could not delete script'));
    }
  };

  const openSaveScriptLibraryModal = () => {
    if (!tenantId) {
      toast.error('Tenant not ready; try again.');
      return;
    }
    if (!composeData.subject.trim() && !composeData.body.trim()) {
      toast.error('Add a subject or message body before saving a script.');
      return;
    }
    const guess = composeData.subject.trim().slice(0, 200) || 'Email script';
    setScriptSaveTitle(guess);
    setScriptSaveCategory('');
    setScriptSaveTags('');
    setScriptSaveUseCase('');
    setScriptSaveOpen(true);
  };

  const submitSaveScriptLibrary = async () => {
    if (!tenantId) return;
    const title = scriptSaveTitle.trim();
    const category = scriptSaveCategory.trim();
    if (!title) {
      toast.error('Title is required.');
      return;
    }
    const canonCategory = canonicalEaScriptLibraryCategory(scriptSaveCategory);
    if (!canonCategory) {
      toast.error('Choose a category from the list.');
      return;
    }
    const tagParts = normalizeEaScriptLibraryTagsFromString(scriptSaveTags);
    if (tagParts.length > EA_SCRIPT_LIBRARY_MAX_TAGS) {
      toast.error(`Use at most ${EA_SCRIPT_LIBRARY_MAX_TAGS} distinct tags.`);
      return;
    }
    try {
      await createEaScheduleMessageScript({
        tenantId,
        title: title.slice(0, 200),
        category: canonCategory,
        tags: tagParts,
        useCase: scriptSaveUseCase.trim().slice(0, 4000),
        subject: composeData.subject,
        body: composeData.body,
      }).unwrap();
      toast.success('Script saved to library.');
      setScriptSaveOpen(false);
    } catch (e: unknown) {
      toast.error(eaRtkErrorDetail(e, 'Could not save script'));
    }
  };

  return (
    <>
    <Modal show={show} onHide={onHide} size="lg" centered className="compose-modal">
      <Modal.Header className="d-flex align-items-center justify-content-between">
        <Modal.Title>Schedule one-off email</Modal.Title>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={onHide}>
            <X size={16} />
          </Button>
        </div>
      </Modal.Header>
      <input
        ref={scheduleAttachmentInputRef}
        type="file"
        className="d-none"
        multiple
        onChange={(e) => void handleAttachmentInputChange(e)}
        aria-hidden
        tabIndex={-1}
      />

      <Modal.Body className="p-0">
        <div className="p-3 border-bottom">
          <Form.Group className="mb-3">
            <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
              <Form.Label className="small fw-bold mb-0">When to send</Form.Label>
              <span className="small text-muted">Based on your device's time</span>
              <OverlayTrigger
                trigger="click"
                rootClose
                placement="bottom-start"
                overlay={
                  <Popover id="ea-schedule-when-info">
                    <Popover.Body className="small" style={{ maxWidth: 320 }}>
                      <p className="mb-2">
                        <strong>Date</strong> and <strong>time</strong> are required and are not pre-filled (to avoid the old “tomorrow by default” mistake).
                      </p>
                      <p className="mb-0">
                        <strong>Time zone</strong> defaults from your device and stays editable. The UTC line below confirms the exact send moment.
                      </p>
                    </Popover.Body>
                  </Popover>
                }
              >
                <Button
                  type="button"
                  variant="link"
                  className="p-0 lh-1 text-primary"
                  style={{ minWidth: '1.25rem' }}
                  aria-label="When to send — more info"
                >
                  <InfoCircle size={14} />
                </Button>
              </OverlayTrigger>
            </div>
            {sendDate.trim() && sendTime.trim() && !isValidIanaTimeZone(tzEffective) ? (
              <Alert variant="secondary" className="small py-2 mb-2">
                Enter a valid IANA time zone (or clear the field to use {deviceTimeZoneId}).
              </Alert>
            ) : null}
            {scheduleSendExplicit ? (
              <div className="small text-muted mb-2">
                <span className="fw-semibold text-body">Will send (UTC):</span> {formatSendAtUtcLine(scheduleSendExplicit.toISOString())}
                <span className="mx-1">·</span>
                <span className="fw-semibold text-body">Your device local:</span> {formatSendAtLocalLine(scheduleSendExplicit.toISOString())}
              </div>
            ) : null}
            <div className="d-flex flex-wrap flex-md-nowrap gap-2 align-items-end">
              <div style={{ flex: '0 0 auto', minWidth: '9rem' }}>
                <Form.Label className="small text-muted mb-1">Date (required)</Form.Label>
                <Form.Control type="date" value={sendDate} onChange={(e) => setSendDate(e.target.value)} size="sm" required />
              </div>
              <div style={{ flex: '0 0 auto', minWidth: '7rem' }}>
                <Form.Label className="small text-muted mb-1">Time (required)</Form.Label>
                <Form.Control type="time" value={sendTime} onChange={(e) => setSendTime(e.target.value)} size="sm" step={60} required />
              </div>
              <div style={{ flex: '1 1 11rem', minWidth: '11rem' }}>
                <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                  Time zone
                  <OverlayTrigger
                    trigger="click"
                    rootClose
                    placement="top"
                    overlay={
                      <Popover id="ea-schedule-tz-info-popover">
                        <Popover.Body className="small" style={{ maxWidth: 300 }}>
                          {scheduleTzPopoverBody}
                          <hr className="my-2" />
                          <span className="text-muted">
                            Defaults from this device; edit if the send should use a different IANA time zone.
                          </span>
                        </Popover.Body>
                      </Popover>
                    }
                  >
                    <Button
                      type="button"
                      variant="link"
                      className="p-0 lh-1 text-primary"
                      style={{ minWidth: '1.25rem' }}
                      aria-label="Time zone help"
                    >
                      <InfoCircle size={14} />
                    </Button>
                  </OverlayTrigger>
                </Form.Label>
                <InputGroup size="sm">
                  <InputGroup.Text
                    className="fw-semibold justify-content-center text-body-secondary"
                    style={{ minWidth: '3.25rem', maxWidth: '4rem' }}
                    title="Abbreviation for the scheduled date & time in this zone (from your browser)"
                  >
                    {scheduleTzShortLabel || '—'}
                  </InputGroup.Text>
                  <Form.Control
                    list="ea-schedule-tz-datalist"
                    value={sendTimezone}
                    onChange={(e) => setSendTimezone(e.target.value)}
                    onBlur={() => {
                      if (!sendTimezone.trim()) setSendTimezone(deviceTimeZoneId);
                    }}
                    placeholder={deviceTimeZoneId}
                    spellCheck={false}
                    autoComplete="off"
                    aria-label="Time zone for when to send"
                  />
                </InputGroup>
                <datalist id="ea-schedule-tz-datalist">
                  {EA_SCHEDULE_COMMON_TIMEZONES.map((z) => (
                    <option key={z} value={z} />
                  ))}
                </datalist>
              </div>
            </div>
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label className="small fw-bold">Related company (optional)</Form.Label>
            <CompanyTypeahead
              value={company}
              onChange={setCompany}
              tenantId={tenantId}
              placeholder="Start typing — pick from your CRM or type a new name"
            />
            <Form.Text className="text-muted" style={{ fontSize: '0.78rem' }}>
              Suggestions come from your Accounts, Target Accounts, and Clients. Internal tag only — not sent to the recipient.
            </Form.Text>
          </Form.Group>
        </div>

        <div className="p-3 border-bottom">
          <Form.Group className="mb-2">
            <Form.Label className="small fw-bold">Assistant sender</Form.Label>
            <Form.Select value={assistantId} onChange={(e) => setAssistantId(e.target.value)} size="sm">
              {assistants.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.firstName} {a.lastName}
                  {a.email ? ` <${a.email}>` : ''}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small fw-bold">To:</Form.Label>
            <Form.Control
              type="text"
              placeholder="Recipients"
              value={composeData.to}
              onChange={(e) => handleComposeChange('to', e.target.value)}
              size="sm"
            />
          </Form.Group>
          <div className="d-flex gap-3">
            <Form.Group className="flex-grow-1">
              <Form.Label className="small fw-bold">Cc:</Form.Label>
              <Form.Control
                type="text"
                placeholder="Cc"
                value={composeData.cc}
                onChange={(e) => handleComposeChange('cc', e.target.value)}
                size="sm"
              />
            </Form.Group>
            <Form.Group className="flex-grow-1">
              <Form.Label className="small fw-bold">Bcc:</Form.Label>
              <Form.Control
                type="text"
                placeholder="Bcc"
                value={composeData.bcc}
                onChange={(e) => handleComposeChange('bcc', e.target.value)}
                size="sm"
              />
            </Form.Group>
          </div>
        </div>

        <div className="p-3 border-bottom">
          <Form.Group>
            <div className="d-flex align-items-center justify-content-between">
              <Form.Label className="small fw-bold mb-0">Subject:</Form.Label>
              {composeConfidentialMode ? (
                <Badge bg="dark" style={{ fontSize: '0.65rem' }}>
                  Confidential
                </Badge>
              ) : null}
            </div>
            <Form.Control
              type="text"
              placeholder="Subject"
              value={composeData.subject}
              onChange={(e) => handleComposeChange('subject', e.target.value)}
              size="sm"
            />
          </Form.Group>
        </div>

        <div className="px-3 pt-2 pb-2 border-bottom ea-schedule-scripts-bar ea-script-library-panel">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className="ea-script-library-heading fw-semibold text-muted mb-0">Script Library</span>
              <OverlayTrigger
                trigger="click"
                rootClose
                placement="bottom-start"
                container={typeof document !== 'undefined' ? document.body : undefined}
                overlay={
                  <Popover id="ea-schedule-placeholders-guide" className="ea-schedule-placeholders-popover">
                    <Popover.Header as="div" className="py-2 small fw-semibold">
                      {'{{ }}'} placeholders
                    </Popover.Header>
                    <Popover.Body className="small py-2">
                      <p className="text-muted mb-2 mb-0" style={{ fontSize: '0.8rem' }}>
                        These tokens are replaced when you <strong>load a script</strong> or click <strong>Schedule</strong>. You can type them directly
                        in subject or body.
                      </p>
                      <hr className="my-2" />
                      <div className="fw-semibold mb-1" style={{ fontSize: '0.72rem' }}>
                        Recipient (from <strong>To</strong>)
                      </div>
                      <dl className="mb-2 mb-0" style={{ fontSize: '0.78rem' }}>
                        <dt className="mb-0">
                          <code className="user-select-all">{'{{firstName}}'}</code>
                        </dt>
                        <dd className="text-muted ms-0 mb-1">First name parsed from To (same as below).</dd>
                        <dt className="mb-0">
                          <code className="user-select-all">{'{{recipientFirstName}}'}</code>
                        </dt>
                        <dd className="text-muted ms-0 mb-0">Same as <code>{'{{firstName}}'}</code>. Example: <code>Jane Doe &lt;j@x.com&gt;</code> → Jane.</dd>
                      </dl>
                      <hr className="my-2" />
                      <div className="fw-semibold mb-1" style={{ fontSize: '0.72rem' }}>
                        Assistant (from <strong>Assistant sender</strong> above)
                      </div>
                      <dl className="mb-0" style={{ fontSize: '0.78rem' }}>
                        <dt className="mb-0">
                          <code className="user-select-all">{'{{assistantName}}'}</code>
                        </dt>
                        <dd className="text-muted ms-0 mb-1">Assistant first + last name.</dd>
                        <dt className="mb-0">
                          <code className="user-select-all">{'{{assistantFirstName}}'}</code>
                        </dt>
                        <dd className="text-muted ms-0 mb-1">Assistant first name only.</dd>
                        <dt className="mb-0">
                          <code className="user-select-all">{'{{senderName}}'}</code>
                        </dt>
                        <dd className="text-muted ms-0 mb-1">Same as full name (alias of <code>{'{{assistantName}}'}</code>).</dd>
                        <dt className="mb-0">
                          <code className="user-select-all">{'{{executiveAssistantName}}'}</code>
                        </dt>
                        <dd className="text-muted ms-0 mb-1">Same as full name (alias).</dd>
                        <dt className="mb-0">
                          <code className="user-select-all">{'{{excutiveAssistantname}}'}</code>
                        </dt>
                        <dd className="text-muted ms-0 mb-0">Same as full name (typo-friendly alias).</dd>
                      </dl>
                    </Popover.Body>
                  </Popover>
                }
              >
                <Button
                  variant="outline-secondary"
                  size="sm"
                  type="button"
                  aria-label="Show placeholder examples"
                  className="ea-script-library-btn"
                >
                  <InfoCircle size={12} className="me-1" aria-hidden />
                  {'{{ }}'} examples
                </Button>
              </OverlayTrigger>
            </div>
            <div className="d-flex align-items-center gap-1 flex-shrink-0">
              {messageScripts.length > 0 ? (
                <Button
                  variant={scriptPanelOpen ? 'outline-secondary' : 'outline-primary'}
                  size="sm"
                  type="button"
                  onClick={() => setScriptPanelOpen((v) => !v)}
                  aria-expanded={scriptPanelOpen}
                  aria-controls="ea-script-library-body"
                  className="ea-script-library-btn"
                >
                  {scriptPanelOpen ? (
                    <>
                      <ChevronUp className="me-1" size={12} />
                      Hide library
                    </>
                  ) : (
                    <>
                      <ChevronDown className="me-1" size={12} />
                      Browse {messageScripts.length.toLocaleString()} script{messageScripts.length === 1 ? '' : 's'}
                    </>
                  )}
                </Button>
              ) : null}
              <Button
                variant="outline-primary"
                size="sm"
                type="button"
                disabled={savingScript}
                onClick={openSaveScriptLibraryModal}
                className="ea-script-library-btn"
              >
                <BookmarkPlus className="me-1" size={12} />
                {savingScript ? 'Saving…' : 'Save as script'}
              </Button>
            </div>
          </div>
          {!scriptPanelOpen ? (
            <div className="small text-muted ea-script-library-hint mb-0">
              {loadingMessageScripts ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" />
                  Loading scripts…
                </span>
              ) : messageScriptsError ? (
                <span className="text-danger">
                  {eaRtkErrorDetail(messageScriptsErr, 'Could not load message scripts.')}
                </span>
              ) : messageScripts.length === 0 ? (
                <span>
                  No scripts yet — compose a message, then <strong>Save as script</strong> (title and category required).
                </span>
              ) : (
                <span>
                  Click <strong>Browse</strong> to search and filter your script library. Placeholders: <strong>{'{{ }}'} examples</strong>.
                </span>
              )}
            </div>
          ) : null}
          {scriptPanelOpen ? (
          <div id="ea-script-library-body">
          <p className="text-muted mb-1 ea-script-library-hint">
            Search and filter, then click a row to preview full content or load into the composer. Placeholders: <strong>{'{{ }}'} examples</strong>.
          </p>
          {messageScriptsError ? (
            <Alert variant="warning" className="py-2 small mb-2 mb-sm-1">
              {eaRtkErrorDetail(messageScriptsErr, 'Could not load message scripts.')}
            </Alert>
          ) : null}
          {loadingMessageScripts ? (
            <div className="d-flex align-items-center gap-2 small text-muted py-1">
              <Spinner animation="border" size="sm" />
              Loading scripts…
            </div>
          ) : messageScripts.length === 0 ? (
            <span className="small text-muted">No scripts yet — compose a message, then <strong>Save as script</strong> (title and category required).</span>
          ) : (
            <>
              <div className="ea-script-library-filter-bar d-flex flex-wrap align-items-center gap-1 mb-1">
                <Form.Control
                  type="search"
                  size="sm"
                  placeholder="Search title, subject, body, tags, category…"
                  value={scriptSearchQuery}
                  onChange={(e) => setScriptSearchQuery(e.target.value)}
                  className="ea-schedule-scripts-search"
                  style={{ minWidth: 0, flex: '1 1 180px' }}
                  aria-label="Search script library"
                />
                <Form.Select
                  size="sm"
                  value={scriptFilterCategory}
                  onChange={(e) => setScriptFilterCategory(e.target.value)}
                  style={{ minWidth: '8rem', maxWidth: '12rem' }}
                  aria-label="Filter by category"
                >
                  <option value="">All categories</option>
                  {scriptCategoryOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Form.Select>
                <Form.Select
                  size="sm"
                  value={scriptFilterStatus}
                  onChange={(e) => setScriptFilterStatus(e.target.value)}
                  style={{ minWidth: '7rem' }}
                  aria-label="Filter by status"
                >
                  <option value="">All statuses</option>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </Form.Select>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Tags (comma, match all)"
                  value={scriptFilterTags}
                  onChange={(e) => setScriptFilterTags(e.target.value)}
                  style={{ minWidth: 0, flex: '1 1 140px' }}
                  aria-label="Filter by tags"
                />
                <Form.Select
                  size="sm"
                  value={scriptSort}
                  onChange={(e) => setScriptSort(e.target.value as EaScriptSortMode)}
                  className="ea-schedule-scripts-sort"
                  style={{ minWidth: '10rem' }}
                  aria-label="Sort scripts"
                >
                  <option value="updated_desc">Newest updated</option>
                  <option value="updated_asc">Oldest updated</option>
                  <option value="created_desc">Newest created</option>
                  <option value="created_asc">Oldest created</option>
                  <option value="use_count_desc">Most used</option>
                  <option value="title_asc">Title A–Z</option>
                  <option value="title_desc">Title Z–A</option>
                </Form.Select>
              </div>
              <div className="ea-script-library-count text-muted">
                {(() => {
                  const total = messageScripts.length;
                  const matched = filteredSortedScripts.length;
                  const shown = Math.min(matched, scriptVisibleLimit);
                  if (matched === 0) return `0 of ${total.toLocaleString()} scripts`;
                  if (matched === total && shown === total) {
                    return `${total.toLocaleString()} script${total === 1 ? '' : 's'}`;
                  }
                  if (shown < matched) {
                    return `Showing ${shown.toLocaleString()} of ${matched.toLocaleString()}${
                      matched === total ? '' : ` (of ${total.toLocaleString()} total)`
                    } scripts`;
                  }
                  return `${matched.toLocaleString()} of ${total.toLocaleString()} scripts`;
                })()}
              </div>
              {filteredSortedScripts.length === 0 ? (
                <span className="small text-muted">No scripts match your filters.</span>
              ) : (
                <ListGroup variant="flush" className="ea-script-library-rows border rounded overflow-hidden mb-0">
                  {filteredSortedScripts.slice(0, scriptVisibleLimit).map((s) => {
                    const subj = (s.subject || '').trim();
                    const rawSnippet =
                      subj || (s.body || '').replace(/\s+/g, ' ').trim();
                    const maxSnip = 72;
                    const preview = rawSnippet.slice(0, maxSnip);
                    const previewEllipsis = rawSnippet.length > maxSnip;
                    const st = String(s.status || 'active');
                    const tagShow = (s.tags || []).slice(0, 3);
                    const uc = Number(s.useCount) || 0;
                    return (
                      <ListGroup.Item
                        key={s.id}
                        action
                        as="div"
                        className="ea-script-library-row px-2 py-1"
                        role="button"
                        tabIndex={0}
                        onClick={() => setScriptPreview(s)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setScriptPreview(s);
                          }
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 min-w-0">
                          <div className="flex-grow-1 min-w-0 d-flex align-items-center gap-2 flex-wrap">
                            <span className="ea-script-library-row-title text-truncate" title={s.title}>
                              {s.title}
                            </span>
                            <span className="ea-script-library-row-meta d-flex align-items-center gap-1 flex-shrink-0">
                              <Badge bg="secondary" className="ea-script-library-badge-cat">
                                {scriptLibraryCategoryBadgeLabel(s.category)}
                              </Badge>
                              <Badge
                                bg={st === 'active' ? 'success' : st === 'draft' ? 'warning' : 'dark'}
                                className="ea-script-library-badge-st"
                              >
                                {st}
                              </Badge>
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-1 flex-shrink-0">
                            <time className="ea-script-library-row-date text-nowrap" dateTime={s.updatedAt}>
                              {formatEaScriptUpdatedLabel(s.updatedAt) || '—'}
                            </time>
                            {uc > 0 ? <span className="ea-script-library-use-nudge text-nowrap">{uc}×</span> : null}
                            <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
                              <Dropdown.Toggle
                                variant="light"
                                size="sm"
                                className="ea-script-library-row-menu py-0 px-1 border-0 text-muted"
                                aria-label="Script actions"
                              >
                                <ThreeDotsVertical size={14} />
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item onClick={() => setScriptPreview(s)}>Preview</Dropdown.Item>
                                <Dropdown.Item onClick={() => void loadScriptIntoComposer(s)}>Load into composer</Dropdown.Item>
                                <Dropdown.Divider />
                                {st !== 'archived' ? (
                                  <Dropdown.Item onClick={() => void setScriptLibraryStatus(s, 'archived')}>Archive</Dropdown.Item>
                                ) : (
                                  <Dropdown.Item onClick={() => void setScriptLibraryStatus(s, 'active')}>Restore</Dropdown.Item>
                                )}
                                {st !== 'draft' ? (
                                  <Dropdown.Item onClick={() => void setScriptLibraryStatus(s, 'draft')}>Mark draft</Dropdown.Item>
                                ) : (
                                  <Dropdown.Item onClick={() => void setScriptLibraryStatus(s, 'active')}>Mark active</Dropdown.Item>
                                )}
                                <Dropdown.Divider />
                                <Dropdown.Item className="text-danger" onClick={() => void removeScriptFromLibrary(s)}>
                                  Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                        </div>
                        {(preview || tagShow.length > 0 || (s.useCase || '').trim()) && (
                          <div className="ea-script-library-row-sub d-flex flex-column gap-0 min-w-0 mt-0">
                            <div className="d-flex align-items-baseline gap-2 min-w-0">
                              {preview ? (
                                <span className="text-truncate flex-grow-1 min-w-0" title={rawSnippet}>
                                  {preview}
                                  {previewEllipsis ? '…' : ''}
                                </span>
                              ) : (
                                <span className="flex-grow-1" />
                              )}
                              {tagShow.length ? (
                                <span className="ea-script-library-row-tags text-truncate flex-shrink-0">
                                  {tagShow.join(' · ')}
                                  {(s.tags || []).length > 3 ? ` +${(s.tags || []).length - 3}` : ''}
                                </span>
                              ) : null}
                            </div>
                            {(s.useCase || '').trim() ? (
                              <div
                                className="ea-script-library-row-usecase small text-muted text-truncate"
                                title={(s.useCase || '').trim()}
                              >
                                <span className="fw-semibold me-1">When:</span>
                                {(s.useCase || '').trim()}
                              </div>
                            ) : null}
                          </div>
                        )}
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              )}
              {filteredSortedScripts.length > scriptVisibleLimit ? (
                <div className="d-flex align-items-center justify-content-center mt-2">
                  <Button
                    variant="link"
                    size="sm"
                    type="button"
                    className="text-decoration-none"
                    onClick={() =>
                      setScriptVisibleLimit((n) =>
                        Math.min(
                          filteredSortedScripts.length,
                          n + EA_SCRIPT_LIBRARY_PAGE_SIZE
                        )
                      )
                    }
                  >
                    Show {Math.min(
                      EA_SCRIPT_LIBRARY_PAGE_SIZE,
                      filteredSortedScripts.length - scriptVisibleLimit
                    ).toLocaleString()} more
                    <ChevronDown className="ms-1" size={14} />
                  </Button>
                </div>
              ) : null}
            </>
          )}
          </div>
          ) : null}
        </div>

        <div className="p-3">
          <Form.Group>
            <Form.Control
              as="textarea"
              rows={8}
              ref={scheduleBodyRef}
              placeholder="Compose your message..."
              value={composeData.body}
              onChange={(e) => handleComposeChange('body', e.target.value)}
              className="border-0"
              style={{ resize: 'none' }}
            />
          </Form.Group>
        </div>

        {composeAttachments.length > 0 ? (
          <div className="px-3 pb-3 border-top">
            <div className="small fw-bold mb-2">Attachments ({composeAttachments.length})</div>
            <div className="d-flex flex-column gap-2">
              {composeAttachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="d-flex align-items-center justify-content-between border rounded px-2 py-1 bg-light"
                >
                  <div className="text-truncate me-2" style={{ maxWidth: '85%' }}>
                    <Paperclip size={12} className="me-1" />
                    <span>{attachment.name}</span>
                    <span className="text-muted ms-1" style={{ fontSize: '0.72rem' }}>
                      ({formatScheduleAttachmentSize(attachment.size)})
                    </span>
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    onClick={() => setComposeAttachments((prev) => prev.filter((x) => x.id !== attachment.id))}
                    title="Remove attachment"
                  >
                    <X size={11} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </Modal.Body>

      <div className="p-2 border-top bg-light">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Button variant="outline-secondary" size="sm" type="button" onClick={() => wrapSelection('**', '**', 'bold text')} title="Bold">
            <strong>B</strong>
          </Button>
          <Button variant="outline-secondary" size="sm" type="button" onClick={() => wrapSelection('*', '*', 'italic text')} title="Italic">
            <em>I</em>
          </Button>
          <Button variant="outline-secondary" size="sm" type="button" onClick={() => wrapSelection('__', '__', 'underlined text')} title="Underline">
            <u>U</u>
          </Button>
          <div className="vr d-none d-sm-block" />
          <Button variant="outline-secondary" size="sm" type="button" onClick={() => openAttachmentPicker('*/*')} title="Attach files">
            <Paperclip size={14} />
          </Button>
          <Button variant="outline-secondary" size="sm" type="button" onClick={handleInsertLink} title="Insert link">
            <Link size={14} />
          </Button>
          <Button variant="outline-secondary" size="sm" type="button" onClick={handleInsertEmoji} title="Insert emoji">
            <EmojiSmile size={14} />
          </Button>
          <Button variant="outline-secondary" size="sm" type="button" onClick={() => openAttachmentPicker('image/*')} title="Attach image">
            <Image size={14} />
          </Button>
          <Button
            variant={composeConfidentialMode ? 'dark' : 'outline-secondary'}
            size="sm"
            type="button"
            onClick={handleToggleConfidential}
            title="Toggle confidential subject prefix"
          >
            <Lock size={14} />
          </Button>
          <Button variant="outline-secondary" size="sm" type="button" onClick={handleInsertScheduleNote} title="Insert schedule note">
            <Calendar size={14} />
          </Button>
          <div className="vr ms-auto" />
          <Button variant="outline-secondary" size="sm" type="button" onClick={handleClearDraft} title="Clear draft">
            <Trash size={14} />
          </Button>
        </div>
      </div>

      <Modal.Footer className="d-flex justify-content-between">
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={
              scheduling ||
              !composeData.to.trim() ||
              !composeData.subject.trim() ||
              !sendDate.trim() ||
              !sendTime.trim() ||
              !isValidIanaTimeZone(tzEffective) ||
              composeAttachments.length > 0
            }
          >
            {scheduling ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                Scheduling…
              </>
            ) : (
              <>
                <Send className="me-2" size={16} />
                Schedule
              </>
            )}
          </Button>
          <Button variant="outline-secondary" onClick={onHide}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>

    <Modal show={Boolean(scriptPreview)} onHide={() => setScriptPreview(null)} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="text-truncate me-2">{scriptPreview?.title || 'Script preview'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {scriptPreview ? (
          <>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <Badge bg="secondary">{scriptLibraryCategoryBadgeLabel(scriptPreview.category)}</Badge>
              <Badge bg={scriptPreview.status === 'active' ? 'success' : scriptPreview.status === 'draft' ? 'warning' : 'dark'}>
                {String(scriptPreview.status || 'active')}
              </Badge>
              {Number(scriptPreview.useCount) > 0 ? (
                <span className="small text-muted">{scriptPreview.useCount} recorded uses</span>
              ) : null}
            </div>
            {(scriptPreview.tags || []).length ? (
              <div className="d-flex flex-wrap gap-1 mb-3">
                {(scriptPreview.tags || []).map((t) => (
                  <Badge key={t} bg="light" text="dark" className="fw-normal border">
                    {t}
                  </Badge>
                ))}
              </div>
            ) : null}
            {scriptPreview.useCase ? (
              <div className="mb-3">
                <div className="small fw-semibold text-muted mb-1">When to use</div>
                <div className="small border rounded p-2 bg-light" style={{ whiteSpace: 'pre-wrap' }}>
                  {scriptPreview.useCase}
                </div>
              </div>
            ) : null}
            <div className="mb-2">
              <div className="small fw-semibold text-muted mb-1">Subject</div>
              <div className="small border rounded p-2 font-monospace bg-white" style={{ whiteSpace: 'pre-wrap' }}>
                {scriptPreview.subject || '—'}
              </div>
            </div>
            <div>
              <div className="small fw-semibold text-muted mb-1">Body</div>
              <div
                className="small border rounded p-2 font-monospace bg-white ea-script-library-preview-body"
                style={{ whiteSpace: 'pre-wrap', maxHeight: 'min(40vh, 280px)', overflow: 'auto' }}
              >
                {scriptPreview.body || '—'}
              </div>
            </div>
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => setScriptPreview(null)}>
          Close
        </Button>
        <Button
          variant="primary"
          disabled={!scriptPreview}
          onClick={() => scriptPreview && void loadScriptIntoComposer(scriptPreview)}
        >
          Load into composer
        </Button>
      </Modal.Footer>
    </Modal>

    <Modal show={scriptSaveOpen} onHide={() => setScriptSaveOpen(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Save to Script Library</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted mb-3">
          Category is fixed to a small set so search and future automation stay reliable. Tags are optional (comma-separated), stored lowercase, max{' '}
          {EA_SCRIPT_LIBRARY_MAX_TAGS}. Describe <strong>when</strong> to use the script so assistants and AI can reason about it later.
        </p>
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">
            Title <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            size="sm"
            value={scriptSaveTitle}
            onChange={(e) => setScriptSaveTitle(e.target.value)}
            placeholder="Short name in the library"
            maxLength={200}
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">
            Category <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            size="sm"
            value={scriptSaveCategory}
            onChange={(e) => setScriptSaveCategory(e.target.value)}
            aria-label="Script category"
          >
            <option value="">Select category…</option>
            {EA_SCRIPT_LIBRARY_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">Tags (optional)</Form.Label>
          <Form.Control
            size="sm"
            list="ea-script-library-tags-datalist"
            value={scriptSaveTags}
            onChange={(e) => setScriptSaveTags(e.target.value)}
            placeholder="vip, renewal, q4"
            aria-label="Script tags"
          />
          <datalist id="ea-script-library-tags-datalist">
            {scriptLibraryTagSuggestions.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
          <Form.Text className="text-muted">Existing tags from your library appear as suggestions.</Form.Text>
        </Form.Group>
        <Form.Group className="mb-0">
          <Form.Label className="small fw-semibold">When to use this script</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            size="sm"
            value={scriptSaveUseCase}
            onChange={(e) => setScriptSaveUseCase(e.target.value)}
            placeholder="e.g. After a discovery call when they asked for pricing, or when re-engaging a quiet client."
            maxLength={4000}
          />
          <Form.Text className="text-muted">Shown in the list as a short “When:” line; optional but strongly recommended.</Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => setScriptSaveOpen(false)}>
          Cancel
        </Button>
        <Button variant="primary" disabled={savingScript} onClick={() => void submitSaveScriptLibrary()}>
          {savingScript ? 'Saving…' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
}

function LinkedChips({ task }: { task: ExecutiveTask }) {
  const chips: { label: string; value: string }[] = [];
  if (task.linkedCampaignId) chips.push({ label: 'Campaign', value: task.linkedCampaignId });
  if (task.accountId) chips.push({ label: 'Account', value: task.accountId });
  if (task.clientId) chips.push({ label: 'Client', value: task.clientId });
  if (task.contactId) chips.push({ label: 'Contact', value: task.contactId });
  if (!chips.length) return <span className="text-muted">None</span>;
  return (
    <div className="d-flex flex-wrap gap-1">
      {chips.map((c) => (
        <Badge key={c.label} bg="light" text="dark" className="border fw-normal">
          {c.label}: {c.value}
        </Badge>
      ))}
    </div>
  );
}

function TaskDetailOffcanvas({
  task,
  assistant,
  onHide,
  onEdit,
  onDelete,
  deleting = false,
}: {
  task: ExecutiveTask | null;
  assistant: ExecutiveAssistantProfile | undefined;
  onHide: () => void;
  onEdit: (t: ExecutiveTask) => void;
  onDelete: (t: ExecutiveTask) => void | Promise<void>;
  deleting?: boolean;
}) {
  if (!task) return null;
  const p = task.progress;
  return (
    <Offcanvas show onHide={onHide} placement="end" style={{ width: 'min(480px, 100vw)' }}>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Task detail</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <div className="d-grid gap-2 mb-3">
          <Button variant="outline-primary" size="sm" onClick={() => onEdit(task)}>
            Edit task
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            disabled={deleting}
            onClick={() => void onDelete(task)}
          >
            {deleting ? 'Deleting…' : 'Delete task'}
          </Button>
        </div>
        <h6 className="fw-semibold">{task.name}</h6>
        <p className="small text-muted mb-3">{task.objective}</p>
        <dl className="small row mb-0">
          <dt className="col-5 text-muted">Assistant</dt>
          <dd className="col-7">{assistant ? `${assistant.firstName} ${assistant.lastName}` : '—'}</dd>
          <dt className="col-5 text-muted">Type</dt>
          <dd className="col-7 text-capitalize">{task.type.replace(/_/g, ' ')}</dd>
          <dt className="col-5 text-muted">Scope</dt>
          <dd className="col-7">
            <Badge bg={task.scope === 'global' ? 'info' : 'secondary'}>{task.scope === 'global' ? 'Global' : 'Private'}</Badge>
          </dd>
          <dt className="col-5 text-muted">Campaign mode</dt>
          <dd className="col-7 text-capitalize">{task.campaignMode}</dd>
          <dt className="col-5 text-muted">Status</dt>
          <dd className="col-7">
            <Badge bg={TASK_STATUS_VARIANT[task.status]}>{TASK_STATUS_LABEL[task.status]}</Badge>
          </dd>
          <dt className="col-5 text-muted">Campaign</dt>
          <dd className="col-7">{task.linkedCampaignName || '—'}</dd>
          <dt className="col-5 text-muted">Linked IDs</dt>
          <dd className="col-7">
            <LinkedChips task={task} />
          </dd>
          <dt className="col-5 text-muted">Next action</dt>
          <dd className="col-7">{formatShortDate(task.nextActionAt)}</dd>
        </dl>
        <hr />
        <div className="fw-semibold small mb-2">Progress</div>
        <ul className="small list-unstyled">
          <li>Contacts identified: {p.identified}</li>
          <li>Contacts queued: {p.queued}</li>
          <li>Emails sent: {p.emailsSent}</li>
          <li>Replies: {p.replies}</li>
          <li>Follow-ups pending: {p.followUpsPending}</li>
          <li>Completed outcomes: {p.completed}</li>
          <li>Exceptions: {p.errors}</li>
        </ul>
        <p className="small text-muted mb-0">Live automation metrics will appear here as the stack evolves.</p>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

function ScheduledEmailEditModal({
  show,
  email,
  assistants,
  tenantId,
  onHide,
  onSave,
}: {
  show: boolean;
  email: ScheduledEmailRow | null;
  assistants: ExecutiveAssistantProfile[];
  tenantId: string;
  onHide: () => void;
  onSave: (id: string, patch: Partial<ScheduledEmailRow>) => void | Promise<void>;
}) {
  const deviceTimeZoneId = useMemo(() => getDeviceIanaTimeZoneId(), []);
  const [assistantId, setAssistantId] = useState('');
  const [recipient, setRecipient] = useState('');
  const [cc, setCc] = useState('');
  const [company, setCompany] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [replyStatus, setReplyStatus] = useState('');
  const [status, setStatus] = useState<ScheduledEmailStatus>('scheduled');
  const [sendDate, setSendDate] = useState('');
  const [sendTime, setSendTime] = useState('');
  const [sendTimezone, setSendTimezone] = useState(() => getDeviceIanaTimeZoneId());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show || !email) return;
    setAssistantId(email.assistantId);
    setRecipient(email.recipient);
    setCc(email.cc || '');
    setCompany(email.company === '—' ? '' : email.company);
    setSubject(email.subject);
    setBody(email.body || '');
    setReplyStatus(email.replyStatus === '—' ? '' : email.replyStatus);
    setStatus(email.status === 'draft' || email.status === 'scheduled' ? email.status : 'scheduled');
    const storedTz = String(email.sendTimezone || '').trim();
    if (isValidIanaTimeZone(storedTz)) {
      setSendTimezone(storedTz);
      const wall = utcInstantToZonedWallStrings(email.sendAt, storedTz);
      if (wall) {
        setSendDate(wall.dateStr);
        setSendTime(wall.timeStr);
      } else {
        setSendDate('');
        setSendTime('');
      }
    } else {
      setSendTimezone(deviceTimeZoneId);
      const wall = utcInstantToZonedWallStrings(email.sendAt, deviceTimeZoneId);
      if (wall) {
        setSendDate(wall.dateStr);
        setSendTime(wall.timeStr);
      } else {
        setSendDate('');
        setSendTime('');
      }
    }
  }, [show, email, deviceTimeZoneId]);

  const submit = async () => {
    if (!email || !tenantId) return;
    if (!recipient.trim() || !subject.trim()) {
      toast.error('Recipient and subject are required.');
      return;
    }
    if (!assistantId) {
      toast.error('Choose an assistant.');
      return;
    }
    if (!sendDate.trim() || !sendTime.trim()) {
      toast.error('Send date and time are required.');
      return;
    }
    const tz = sendTimezone.trim() || deviceTimeZoneId;
    if (!isValidIanaTimeZone(tz)) {
      toast.error('Enter a valid send time zone, or clear the field to use your device zone.');
      return;
    }
    const sendAtDate = parseScheduleSendAt(sendDate, sendTime, tz);
    if (!sendAtDate) {
      toast.error('Invalid send date or time for this time zone (e.g. non-existent clock time at DST change).');
      return;
    }
    const scriptCtx = buildEaScheduleScriptContext(assistants, assistantId, recipient);
    const subjectOut = applyScriptPlaceholders(subject.trim(), scriptCtx);
    const bodyOut = applyScriptPlaceholders(body.trim(), scriptCtx);
    setSaving(true);
    try {
      await onSave(email.id, {
        assistantId,
        recipient: recipient.trim(),
        cc: cc.trim(),
        company: company.trim() || '—',
        subject: subjectOut,
        body: bodyOut,
        sendAt: sendAtDate.toISOString(),
        sendTimezone: tz,
        status,
        replyStatus: replyStatus.trim() || '—',
      });
    } finally {
      setSaving(false);
    }
  };

  const storedTzRaw = String(email?.sendTimezone || '').trim();
  const hadStoredTz = Boolean(email && storedTzRaw && isValidIanaTimeZone(storedTzRaw));
  const editTzEffective = sendTimezone.trim() || deviceTimeZoneId;
  const editSendPreview = useMemo(() => {
    if (!sendDate.trim() || !sendTime.trim() || !isValidIanaTimeZone(editTzEffective)) return null;
    return parseScheduleSendAt(sendDate, sendTime, editTzEffective);
  }, [sendDate, sendTime, editTzEffective]);

  if (!email) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Edit scheduled email</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="light" className="small border py-2 mb-3">
          <div className="fw-semibold mb-1">When this email will send</div>
          <div>
            <span className="text-muted">Stored instant (UTC):</span> {formatSendAtUtcLine(email.sendAt)}
          </div>
          <div className="text-muted">
            Same instant, your browser local: {formatSendAtLocalLine(email.sendAt)} ({deviceTimeZoneId})
          </div>
          {hadStoredTz ? (
            <div className="mt-2 mb-0">
              Originally scheduled in{' '}
              <strong>{scheduleTimeZoneUserLabel(storedTzRaw, new Date(email.sendAt))}</strong>. The date and time fields below use that zone unless you
              change it.
            </div>
          ) : (
            <div className="mt-2 mb-0 text-warning">
              <p className="mb-2">No time zone was saved for this older scheduled email.</p>
              <p className="mb-2">
                The send time is currently being shown in{' '}
                <strong>{scheduleTimeZoneUserLabel(deviceTimeZoneId, new Date(email.sendAt))}</strong>.
              </p>
              <p className="mb-0">
                The time zone field defaults to your device. If you originally scheduled in a different region, change it before saving so the send
                instant does not shift.
              </p>
            </div>
          )}
        </Alert>
        {!hadStoredTz ? (
          <Alert variant="secondary" className="small py-2 mb-3">
            Date and time below match that display using your device zone. Adjust the time zone only if it should not be{' '}
            <strong>{deviceTimeZoneId}</strong>.
          </Alert>
        ) : null}
        {editSendPreview ? (
          <Alert variant="info" className="small py-2 mb-3">
            <div className="fw-semibold mb-1">From the fields below</div>
            <div>
              Calendar date <strong>{sendDate}</strong> at <strong>{sendTime}</strong> in{' '}
              <strong>{scheduleTimeZoneUserLabel(editTzEffective, editSendPreview)}</strong>
            </div>
            <div className="text-muted mt-1">
              UTC: {formatSendAtUtcLine(editSendPreview.toISOString())} · This device:{' '}
              {formatSendAtLocalLine(editSendPreview.toISOString())}
            </div>
          </Alert>
        ) : null}
        <p className="small text-muted">
          Saving updates the exact send moment from <strong>date + time + time zone</strong>. The UTC line above updates after you save if the send
          moment changes.
        </p>
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">Assistant sender</Form.Label>
          <Form.Select size="sm" value={assistantId} onChange={(e) => setAssistantId(e.target.value)}>
            {assistants.map((a) => (
              <option key={a.id} value={a.id}>
                {a.firstName} {a.lastName}
                {a.email ? ` <${a.email}>` : ''}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">Recipient (To)</Form.Label>
          <Form.Control size="sm" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="name@example.com" />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">Cc</Form.Label>
          <Form.Control size="sm" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Optional, comma-separated" />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">Company (internal)</Form.Label>
          <CompanyTypeahead
            value={company}
            onChange={setCompany}
            tenantId={tenantId}
            placeholder="Pick from your CRM or type a new label"
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">Subject</Form.Label>
          <Form.Control size="sm" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">Message body</Form.Label>
          <Form.Control as="textarea" rows={6} size="sm" value={body} onChange={(e) => setBody(e.target.value)} className="font-monospace" />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">Status</Form.Label>
          <Form.Select size="sm" value={status} onChange={(e) => setStatus(e.target.value as ScheduledEmailStatus)}>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small fw-semibold">Reply tracking (optional)</Form.Label>
          <Form.Control size="sm" value={replyStatus} onChange={(e) => setReplyStatus(e.target.value)} placeholder="—" />
        </Form.Group>
        <Row className="g-2 mb-2">
          <Col xs={12} sm={4}>
            <Form.Group>
              <Form.Label className="small fw-semibold">Send date (required)</Form.Label>
              <Form.Control type="date" size="sm" value={sendDate} onChange={(e) => setSendDate(e.target.value)} />
            </Form.Group>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Group>
              <Form.Label className="small fw-semibold">Send time (required)</Form.Label>
              <Form.Control type="time" size="sm" value={sendTime} onChange={(e) => setSendTime(e.target.value)} />
            </Form.Group>
          </Col>
          <Col xs={12} sm={4}>
            <Form.Group>
              <Form.Label className="small fw-semibold">Time zone</Form.Label>
              <Form.Control
                size="sm"
                list="ea-edit-scheduled-email-tz"
                value={sendTimezone}
                onChange={(e) => setSendTimezone(e.target.value)}
                onBlur={() => {
                  if (!sendTimezone.trim()) setSendTimezone(deviceTimeZoneId);
                }}
                placeholder={deviceTimeZoneId}
                aria-label="Time zone for when to send"
                spellCheck={false}
                autoComplete="off"
              />
              <datalist id="ea-edit-scheduled-email-tz">
                {EA_SCHEDULE_COMMON_TIMEZONES.map((z) => (
                  <option key={z} value={z} />
                ))}
              </datalist>
              <Form.Text className="text-muted" style={{ fontSize: '0.78rem' }}>
                Defaults from this device when empty; edit if needed.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={
            saving ||
            !assistants.length ||
            !sendDate.trim() ||
            !sendTime.trim() ||
            !isValidIanaTimeZone(editTzEffective)
          }
          onClick={() => void submit()}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function ScheduledEmailDetailOffcanvas({
  email,
  assistant,
  onHide,
  onPatch,
  onLogActivity,
  onOpenEdit,
}: {
  email: ScheduledEmailRow | null;
  assistant?: ExecutiveAssistantProfile;
  onHide: () => void;
  onPatch: (id: string, patch: Partial<ScheduledEmailRow>) => void | Promise<void>;
  onLogActivity: (message: string) => void | Promise<void>;
  onOpenEdit: () => void;
}) {
  if (!email) return null;

  const canEditSchedule = email.status === 'draft' || email.status === 'scheduled';
  const canCancel = email.status !== 'canceled' && email.status !== 'sent' && email.status !== 'failed' && email.status !== 'sending';

  const handleReschedule24h = async () => {
    await onPatch(email.id, { sendAt: new Date(Date.now() + 86400_000).toISOString() });
    await onLogActivity(`Rescheduled email "${email.subject}" (+24h).`);
    toast.success('Rescheduled (+24h).');
  };

  const handleCancel = async () => {
    await onPatch(email.id, { status: 'canceled' });
    await onLogActivity(`Canceled scheduled email "${email.subject}".`);
    toast.success('Canceled.');
  };

  return (
    <Offcanvas show onHide={onHide} placement="end" style={{ width: 'min(480px, 100vw)' }}>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Scheduled email</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <h6 className="fw-semibold text-break">{email.subject}</h6>
        <p className="small text-muted mb-0">One-off send — persisted via Executive Assistant API.</p>
        <dl className="small row mt-3 mb-0">
          <dt className="col-5 text-muted">Recipient</dt>
          <dd className="col-7 text-break">{email.recipient}</dd>
          {email.cc ? (
            <>
              <dt className="col-5 text-muted">Cc</dt>
              <dd className="col-7 text-break">{email.cc}</dd>
            </>
          ) : null}
          <dt className="col-5 text-muted">Company</dt>
          <dd className="col-7">{email.company}</dd>
          <dt className="col-5 text-muted">Assistant</dt>
          <dd className="col-7">{assistant ? `${assistant.firstName} ${assistant.lastName}` : '—'}</dd>
          <dt className="col-5 text-muted">Send at</dt>
          <dd className="col-7">{formatShortDate(email.sendAt)}</dd>
          <dt className="col-5 text-muted">Send time zone</dt>
          <dd className="col-7">
            {email.sendTimezone && isValidIanaTimeZone(email.sendTimezone) ? (
              scheduleTimeZoneUserLabel(email.sendTimezone, new Date(email.sendAt))
            ) : (
              <span className="text-muted">Not stored (older send) — edit uses your device zone until you save.</span>
            )}
          </dd>
          <dt className="col-5 text-muted">Status</dt>
          <dd className="col-7">
            <Badge bg={EMAIL_STATUS_VARIANT[email.status]}>{email.status}</Badge>
          </dd>
          <dt className="col-5 text-muted">Reply</dt>
          <dd className="col-7">{email.replyStatus}</dd>
          <dt className="col-5 text-muted">Message</dt>
          <dd className="col-7">
            <pre className="small mb-0 text-wrap font-monospace bg-light border rounded p-2" style={{ whiteSpace: 'pre-wrap' }}>
              {email.body || '—'}
            </pre>
          </dd>
          <dt className="col-5 text-muted">Created by</dt>
          <dd className="col-7">
            <div>{email.createdBy}</div>
            <div className="small text-muted mt-1">
              The KG user who scheduled this (Auth0 account). It is not the assistant name. If you see “Another user (…xxxxxx)”, that suffix is the end
              of their login id.
            </div>
          </dd>
        </dl>
        <hr className="my-4" />
        <div className="d-grid gap-2">
          <Button variant="outline-primary" disabled={!canEditSchedule} onClick={() => (canEditSchedule ? onOpenEdit() : undefined)}>
            Edit
          </Button>
          <Button variant="outline-secondary" disabled={!canEditSchedule} onClick={() => void handleReschedule24h()}>
            Reschedule +24h
          </Button>
          <Button variant="outline-secondary" disabled={!canEditSchedule} onClick={() => toast.info('Send now will connect to outbound delivery.')}>
            Send now
          </Button>
          <Button variant="outline-secondary" onClick={() => toast.info('Thread view will open when Unibox integration is wired.')}>
            View thread
          </Button>
          <Button variant="outline-danger" disabled={!canCancel} onClick={() => void handleCancel()}>
            Cancel send
          </Button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

export default ExecutiveAssistantPage;
